import type { ListingAuthoringRequest, ListingAuthoringResult } from "./listingAuthoring";
import type { ResearchBatchInput, ResearchBatchResult } from "./researchReview";
import { applyCatalogueUpdate, type CatalogueUpdateInput, type CatalogueUpdateResult } from "./catalogue";
import { normalizeDatabase } from "./database";
import type { PhotoSaveStage } from "./photoProgress";
import { markItemsListedManuallyInDatabase, type ManualListingInput, type ManualListingResult } from "./manualListings";
import { paginateWorkspaceCollection, type WorkspaceCollectionPage, type WorkspaceCollectionQuery, type WorkspaceCollectionResource } from "./workspaceCollections";
export type { WorkspaceCollectionPage } from "./workspaceCollections";
import { limitForEntitlement } from "./entitlements";
import { importLocalBackupIntoWorkspace, type LocalBackupImportSkipped, type LocalBackupImportSummary } from "./localBackupImport";
import { emptyDatabase, makeID } from "./seed";
import type { InventoryItem, InventoryItemInput, InventoryItemUpdate, ItemPhoto, ListingDraftUpdate, Marketplace, MarketplaceAccount, MarketplaceImportApplyResult, MarketplaceImportDecisionInput, MarketplaceImportPage, MarketplaceImportRecord, MarketplaceImportRun, MarketplaceImportStatus, MarketplaceIntegrationOperation, MarketplaceListing, PhotoInput, ResearchDepth, ResearchFocus, Sale, SaleStatus, StoredAsset, StoredAssetKind, StoredAssetProvider, ReOSDatabase, SubscriptionPlan, WorkspaceRole } from "./types";
import {
  addItemPhotoToDatabase,
  addItemPhotosToDatabase,
  archiveInventoryItemInDatabase,
  createListingDraftsForItemInDatabase,
  createInventoryWorkflowInDatabase,
  markListingLiveInDatabase,
  recordSaleInDatabase,
  removeItemPhotoFromDatabase,
  updateInventoryItemInDatabase,
  updateItemPhotoInDatabase,
  updateListingDraftInDatabase,
  updateSaleStatusInDatabase,
  updateWorkspaceSettingsInDatabase,
  type SaleFulfilmentUpdate,
  type SaleInput,
  type WorkspaceSettingsUpdate
} from "./workflows";

export const LOCAL_DEMO_STORAGE_KEY = "reos.web.local-demo.database.v2";
export const LEGACY_LOCAL_DEMO_STORAGE_KEYS = ["reos.web.database.v1"] as const;
const API_REQUEST_TIMEOUT_MS = 20_000;
const ITEM_RESEARCH_REQUEST_TIMEOUT_MS = 120_000;
const PHOTO_COMPLETION_REQUEST_TIMEOUT_MS = 240_000;
const PHOTO_UPLOAD_MIN_TIMEOUT_MS = 30_000;
const PHOTO_UPLOAD_MAX_TIMEOUT_MS = 300_000;
const PHOTO_UPLOAD_ASSUMED_BYTES_PER_SECOND = 128 * 1024;
const PHOTO_UPLOAD_SETUP_ALLOWANCE_MS = 15_000;
const WORKSPACE_UNREACHABLE_MESSAGE = "Listwork couldn't reach your workspace. Check your connection and try again.";
const PHOTO_UPLOAD_TIMEOUT_MESSAGE = "Photo upload took too long. Check your connection and try again.";
const UNAVAILABLE_PHOTO_URL =
  "data:image/svg+xml;charset=UTF-8,%3Csvg%20xmlns%3D%22http%3A%2F%2Fwww.w3.org%2F2000%2Fsvg%22%20viewBox%3D%220%200%20120%20120%22%3E%3Crect%20width%3D%22120%22%20height%3D%22120%22%20rx%3D%2212%22%20fill%3D%22%23f2f4f7%22%2F%3E%3Cpath%20d%3D%22M31%2079l18-22%2014%2016%209-11%2017%2017H31z%22%20fill%3D%22%23d0d5dd%22%2F%3E%3Ccircle%20cx%3D%2278%22%20cy%3D%2242%22%20r%3D%228%22%20fill%3D%22%23d0d5dd%22%2F%3E%3C%2Fsvg%3E";

export type ReOSRepositoryMode = "api" | "local_demo" | "unconfigured";

export class ReOSApiError extends Error {
  constructor(
    message: string,
    readonly status: number
  ) {
    super(message);
    this.name = "ReOSApiError";
  }
}

export function isExpiredPhotoReplacementError(error: unknown) {
  return (
    error instanceof ReOSApiError &&
    error.status >= 400 &&
    error.status < 500 &&
    (
      error.message === "Photo replacement upload is no longer active." ||
      error.message === "Photo asset does not exist in this workspace." ||
      error.message === "Upload was not requested or has expired."
    )
  );
}

export interface ReOSRepositoryEnvironment {
  apiBaseURL?: string;
  allowLocalDemo?: string;
  dev?: boolean;
}

export interface PhotoSaveContext {
  replacesPhotoID?: string;
  onProgress?: (stage: PhotoSaveStage) => void;
}

export type ChannelRequestAction = "publication/terms/options" | "publication/policy/options" | "publication/policy/preview" | "publication/policy/create" | "publication/policy/check" | "publication/read" | "publication/draft/save" | "publication/setup" | "publication/categories" | "publication/requirements" | "publication/review" | "publication/submit" | "publication/check" | "activity" | "check" | "settings" | "listing/refresh" | "listing/edit/read" | "listing/edit" | "listing/discard" | "listing/review" | "listing/update" | "listing/update/check" | "photos/preview" | "photos/confirm" | "photos/retry" | "stock" | "draft" | "history/preview" | "history/import" | "history/status";

export interface ReOSRepository {
  authorListing?: (database: ReOSDatabase, listingID: string, request: ListingAuthoringRequest) => Promise<ListingAuthoringResult>;
  spreadsheetRequest?: <T>(database: ReOSDatabase, input: import("./spreadsheetMigration").MigrationRequest) => Promise<T>;
  notificationSettingsRequest?: <T>(database: ReOSDatabase, method: "GET" | "POST" | "DELETE", action?: string, body?: unknown) => Promise<T>;
  channelRequest?: <T>(database: ReOSDatabase, accountID: string, action: ChannelRequestAction, body?: unknown, query?: Record<string, string>) => Promise<T>;
  mode: ReOSRepositoryMode;
  label: string;
  loadDatabase: () => Promise<ReOSDatabase>;
  loadWorkspace: (workspaceID: string, previous?: ReOSDatabase) => Promise<ReOSDatabase>;
  refreshPhotoReadURLs?: (database: ReOSDatabase, forceAssetIDs?: string[]) => Promise<ReOSDatabase>;
  listWorkspaceCollection: (database: ReOSDatabase, resource: WorkspaceCollectionResource, query: WorkspaceCollectionQuery) => Promise<WorkspaceCollectionPage>;
  listAccountWorkspaces: (database?: ReOSDatabase) => Promise<AccountWorkspaceSummary[]>;
  updateWorkspaceSettings: (database: ReOSDatabase, update: WorkspaceSettingsUpdate) => Promise<ReOSDatabase>;
  createPasskeyRegistrationOptions: (input: PasskeyRegistrationOptionsInput) => Promise<PasskeyOptionsResult>;
  completePasskeyRegistration: (input: PasskeyCompletionInput) => Promise<ReOSDatabase>;
  createPasskeyAuthenticationOptions: (input: PasskeyAuthenticationOptionsInput) => Promise<PasskeyOptionsResult>;
  completePasskeyAuthentication: (input: PasskeyCompletionInput) => Promise<ReOSDatabase>;
  requestEmailSignInCode: (input: EmailSignInCodeRequestInput) => Promise<EmailSignInCodeRequestResult>;
  completeEmailSignInCode: (input: EmailSignInCodeCompleteInput) => Promise<ReOSDatabase>;
  signOut: () => Promise<void>;
  listSessions: () => Promise<AccountSession[]>;
  revokeSession: (sessionID: string) => Promise<RevokeSessionResult>;
  listPasskeys: () => Promise<AccountPasskey[]>;
  createAccountPasskeyRegistrationOptions: () => Promise<PasskeyOptionsResult>;
  completeAccountPasskeyRegistration: (input: PasskeyCompletionInput) => Promise<PasskeyMutationResult>;
  revokePasskey: (passkeyID: string) => Promise<PasskeyMutationResult>;
  requestEmailVerification: () => Promise<EmailVerificationRequestResult>;
  completeEmailVerification: (token: string) => Promise<void>;
  exportAccountData: () => Promise<AccountDataExport>;
  accountArchiveDownloadURL: () => string;
  importLocalBackup: (database: ReOSDatabase, backup: unknown) => Promise<LocalBackupImportResult>;
  deleteAccount: (input: DeleteAccountInput) => Promise<AccountDeletionResult>;
  createBillingCheckout: (input: BillingCheckoutInput) => Promise<BillingSessionResult>;
  getAssistantUsage: (database: ReOSDatabase) => Promise<AssistantUsageSummary>;
  createBillingPortal: (input?: BillingPortalInput) => Promise<BillingSessionResult>;
  getEbayIntegration: (database: ReOSDatabase) => Promise<EbayIntegrationStatus>;
  getEbayImportPreview: (database: ReOSDatabase, page?: number, pageSize?: number) => Promise<EbayImportPreview>;
  importEbayListings: (database: ReOSDatabase, input: { externalListingIDs: string[]; page: number; pageSize: number }) => Promise<EbayImportResult>;
  getMarketplaceImportPreview: (database: ReOSDatabase, marketplace: Extract<Marketplace, "ebay" | "etsy">, page?: number, pageSize?: number, runID?: string) => Promise<MarketplaceImportPage>;
  getMarketplaceImportStatus: (database: ReOSDatabase, marketplace: Extract<Marketplace, "ebay" | "etsy">, runID: string, recordIDs: string[]) => Promise<MarketplaceImportStatus>;
  saveMarketplaceImportDecisions: (database: ReOSDatabase, marketplace: Extract<Marketplace, "ebay" | "etsy">, runID: string, decisions: MarketplaceImportDecisionInput[]) => Promise<{ run: MarketplaceImportRun; records: MarketplaceImportRecord[] }>;
  applyMarketplaceImport: (database: ReOSDatabase, marketplace: Extract<Marketplace, "ebay" | "etsy">, runID: string, recordIDs?: string[], expectedRevisions?: Record<string, number>, openingQuantities?: Record<string, number>) => Promise<MarketplaceImportApplyResult>;
  startEbayConnection: (database: ReOSDatabase, returnPath?: string, accessMode?: "read_only" | "read_write", financialAccess?: boolean) => Promise<string>;
  updateEbayIntegration: (database: ReOSDatabase, input: EbayIntegrationConfiguration) => Promise<EbayIntegrationStatus>;
  disconnectEbay: (database: ReOSDatabase) => Promise<EbayIntegrationStatus>;
  getEtsyIntegration: (database: ReOSDatabase) => Promise<EtsyIntegrationStatus>;
  startEtsyConnection: (database: ReOSDatabase, returnPath?: string) => Promise<string>;
  updateEtsyIntegration: (database: ReOSDatabase, input: EtsyIntegrationConfiguration) => Promise<EtsyIntegrationStatus>;
  disconnectEtsy: (database: ReOSDatabase) => Promise<EtsyIntegrationStatus>;
  searchEtsyTaxonomy: (database: ReOSDatabase, query: string) => Promise<EtsyTaxonomyChoice[]>;
  listMarketplaceIntegrations: (database: ReOSDatabase) => Promise<MarketplaceAccount[]>;
  runMarketplaceLifecycle: (
    database: ReOSDatabase,
    marketplace: Marketplace,
    operation: MarketplaceLifecycleOperation,
    idempotencyKey?: string,
    payload?: Record<string, unknown>,
    resourceID?: string
  ) => Promise<MarketplaceLifecycleResult>;
  reconcileMarketplaceLifecycle: (
    database: ReOSDatabase,
    marketplace: Marketplace,
    operation: MarketplaceLifecycleOperation,
    idempotencyKey: string
  ) => Promise<MarketplaceLifecycleResult>;
  listWorkspaceMembers: (database: ReOSDatabase) => Promise<WorkspaceMembersResult>;
  addWorkspaceMember: (database: ReOSDatabase, input: AddWorkspaceMemberInput) => Promise<WorkspaceMembersMutationResult>;
  removeWorkspaceMember: (database: ReOSDatabase, memberID: string) => Promise<WorkspaceMembersMutationResult>;
  listWorkspaceInvitations: (database: ReOSDatabase) => Promise<WorkspaceInvitationsResult>;
  createWorkspaceInvitation: (database: ReOSDatabase, input: CreateWorkspaceInvitationInput) => Promise<WorkspaceInvitationMutationResult>;
  revokeWorkspaceInvitation: (database: ReOSDatabase, invitationID: string) => Promise<WorkspaceInvitationsResult>;
  acceptWorkspaceInvitation: (token: string) => Promise<AcceptWorkspaceInvitationResult>;
  preparePhotoForSave: (
    workspaceID: string,
    photo: PhotoInput,
    index: number,
    context?: PhotoSaveContext
  ) => Promise<PhotoInput>;
  cancelPreparedPhoto: (workspaceID: string, photo: PhotoInput) => Promise<boolean>;
  createInventoryWorkflow: (database: ReOSDatabase, input: InventoryItemInput, photos: PhotoInput[], selectedMarketplaces: Marketplace[]) => Promise<InventoryWorkflowResult>;
  updateCatalogue: (database: ReOSDatabase, input: CatalogueUpdateInput) => Promise<CatalogueUpdateResult>;
  updateInventoryItem: (database: ReOSDatabase, itemID: string, update: InventoryItemUpdate) => Promise<InventoryItemMutationResult>;
  startResearchBatch?: (database: ReOSDatabase, input: ResearchBatchInput) => Promise<ResearchBatchResult>;
  startItemResearch: (database: ReOSDatabase, itemID: string, input: ItemResearchRunRequest) => Promise<InventoryItemMutationResult>;
  refreshItemResearch: (database: ReOSDatabase, itemID: string) => Promise<InventoryItemMutationResult>;
  createListingDrafts: (database: ReOSDatabase, itemID: string, selectedMarketplaces: Marketplace[]) => Promise<ListingDraftsResult>;
  addItemPhoto: (database: ReOSDatabase, itemID: string, photo: PhotoInput) => Promise<ItemPhotoResult>;
  addItemPhotos: (database: ReOSDatabase, itemID: string, photos: PhotoInput[]) => Promise<ItemPhotosResult>;
  updateItemPhoto: (database: ReOSDatabase, photoID: string, photo: PhotoInput) => Promise<ItemPhotoResult>;
  removeItemPhoto: (database: ReOSDatabase, photoID: string) => Promise<ReOSDatabase>;
  updateListingDraft: (database: ReOSDatabase, listingID: string, update: ListingDraftUpdate) => Promise<ListingDraftMutationResult>;
  updateListingURL: (database: ReOSDatabase, listingID: string, externalURL: string) => Promise<ReOSDatabase>;
  publishListing: (database: ReOSDatabase, listingID: string) => Promise<ListingPublishResult>;
  markListingLive: (database: ReOSDatabase, listingID: string, externalURL?: string) => Promise<ReOSDatabase>;
  markItemsListedManually: (database: ReOSDatabase, input: ManualListingInput) => Promise<ManualListingResult>;
  endListing: (database: ReOSDatabase, listingID: string) => Promise<ReOSDatabase>;
  archiveInventoryItem: (database: ReOSDatabase, itemID: string) => Promise<ReOSDatabase>;
  recordSale: (database: ReOSDatabase, input: SaleInput) => Promise<SaleWorkflowResult>;
  updateSaleStatus: (database: ReOSDatabase, saleID: string, status: SaleStatus) => Promise<ReOSDatabase>;
  updateSaleFulfilment: (database: ReOSDatabase, saleID: string, update: SaleFulfilmentUpdate) => Promise<ReOSDatabase>;
  bulkPackSales: (database: ReOSDatabase, saleIDs: string[]) => Promise<BulkSaleUpdateResult>;
  markNotificationRead: (database: ReOSDatabase, notificationID: string) => Promise<ReOSDatabase>;
  saveDatabase: (database: ReOSDatabase) => Promise<void>;
  clearDatabase: () => Promise<void>;
}

export interface InventoryWorkflowResult {
  database: ReOSDatabase;
  item: InventoryItem;
}

export type MarketplaceLifecycleOperation = MarketplaceIntegrationOperation;

export interface MarketplaceLifecycleResult {
  operation: MarketplaceLifecycleOperation;
  marketplace: Marketplace;
  externalID: string;
  status: "accepted" | "completed";
  provider: string;
  mode: "sandbox" | "live";
  evidence: import("./types").MarketplaceIntegrationEvidence;
  occurredAt: string;
  data: Record<string, unknown>;
  integration?: MarketplaceAccount;
}

export interface InventoryItemMutationResult {
  database: ReOSDatabase;
  item: InventoryItem;
}

export interface ItemResearchRunRequest {
  depth: ResearchDepth;
  focus: ResearchFocus;
  customPriority?: string;
  sellerContext?: string;
  photoIDs: string[];
}

export interface ListingDraftsResult {
  database: ReOSDatabase;
  listings: MarketplaceListing[];
}

export interface ListingDraftMutationResult {
  database: ReOSDatabase;
  listing: MarketplaceListing;
}

export interface SaleWorkflowResult {
  database: ReOSDatabase;
  sale: Sale;
}

export interface BulkSaleUpdateResult {
  database: ReOSDatabase;
  updatedSaleIDs: string[];
  skipped: Array<{ saleID: string; reason: string }>;
}

export interface ItemPhotoResult {
  database: ReOSDatabase;
  photo: ItemPhoto;
}

export interface ItemPhotosResult {
  database: ReOSDatabase;
  photos: ItemPhoto[];
}

export interface ListingPublication {
  externalListingID: string;
  externalURL: string;
  externalAccountID?: string;
  publishedAt: string;
  provider: string;
  mode: "live" | "sandbox";
}

export interface ListingPublishResult {
  database: ReOSDatabase;
  publication: ListingPublication;
}

export interface EbayIntegrationConfiguration {
  paymentPolicyID?: string;
  fulfillmentPolicyID?: string;
  returnPolicyID?: string;
  merchantLocationKey?: string;
  verifyNotifications?: boolean;
}

export interface EbayIntegrationChoice {
  id: string;
  name: string;
}

export interface EbayIntegrationStatus {
  configured: boolean;
  environment: "disabled" | "sandbox" | "production" | "simulated";
  accessMode: "read_only" | "read_write";
  finances?: { available: boolean; authorized: boolean };
  publishing?: { authorized: boolean; ready: boolean; missing: string[] };
  marketplaceID: string;
  connection?: {
    username?: string;
    userID: string;
    status: "connected" | "reconnect_required";
    connectedAt: string;
    lastError?: string;
  };
  configuration?: EbayIntegrationConfiguration;
  authorization: {
    authorized: boolean;
    grantedScopes: string[];
    missingScopes: string[];
  };
  notifications: {
    destinationID?: string;
    orderSubscriptionID?: string;
    ready: boolean;
    lastVerifiedAt?: string;
    lastError?: string;
  };
  orderSync?: { lastSuccessAt?: string; lastAttemptAt?: string; orderError?: string; notificationError?: string; nextAttemptAt?: string; window?: { offset: number } };
  choices: {
    paymentPolicies: EbayIntegrationChoice[];
    fulfillmentPolicies: EbayIntegrationChoice[];
    returnPolicies: EbayIntegrationChoice[];
    locations: EbayIntegrationChoice[];
  };
  readiness: {
    ready: boolean;
    missing: string[];
  };
}

export interface EbayImportListing {
  externalListingID: string;
  title: string;
  description?: string;
  sku?: string;
  quantity: number;
  price: ReOSDatabase["inventoryItems"][number]["targetPrice"];
  categoryID?: string;
  categoryName?: string;
  condition?: string;
  externalURL?: string;
  imageURLs: string[];
  listedAt?: string;
  imported: boolean;
}

export interface EbayImportPreview {
  page: number;
  pageSize: number;
  totalPages: number;
  totalEntries: number;
  items: EbayImportListing[];
}

export interface EbayImportResult {
  imported: number;
  skipped: number;
  importedItemIDs: string[];
}

export interface EtsyIntegrationConfiguration {
  shopID?: string;
  shippingProfileID?: string;
  returnPolicyID?: string;
}

export interface EtsyIntegrationChoice {
  id: string;
  name: string;
}

export interface EtsyTaxonomyChoice extends EtsyIntegrationChoice {
  path: string;
}

export interface EtsyIntegrationStatus {
  configured: boolean;
  environment: "disabled" | "production";
  connection?: {
    userID: string;
    shopID?: string;
    shopName?: string;
    status: "connected" | "reconnect_required";
    connectedAt: string;
    lastError?: string;
  };
  authorization: {
    authorized: boolean;
    grantedScopes: string[];
    missingScopes: string[];
  };
  configuration?: EtsyIntegrationConfiguration;
  webhooks: {
    configured: boolean;
    endpoint: string;
  };
  choices: {
    shops: EtsyIntegrationChoice[];
    shippingProfiles: EtsyIntegrationChoice[];
    returnPolicies: EtsyIntegrationChoice[];
  };
  readiness: {
    ready: boolean;
    missing: string[];
  };
}

export interface PasskeyRegistrationOptionsInput {
  email: string;
  name: string;
  workspaceName?: string;
  invitationToken?: string;
  turnstileToken?: string;
}

export interface PasskeyAuthenticationOptionsInput {
  email?: string;
  turnstileToken?: string;
}

export interface PasskeyOptionsResult {
  challengeID: string;
  options: unknown;
}

export interface PasskeyCompletionInput {
  challengeID: string;
  response: unknown;
}

export interface EmailSignInCodeRequestInput {
  email: string;
  name?: string;
  workspaceName?: string;
  invitationToken?: string;
  intent: "create" | "signin";
  turnstileToken?: string;
}

export interface EmailSignInCodeRequestResult {
  ok: boolean;
  code?: string;
}

export interface EmailSignInCodeCompleteInput {
  email: string;
  code: string;
}

export interface AccountSession {
  id: string;
  current: boolean;
  createdAt: string;
  expiresAt: string;
}

export interface AccountPasskey {
  id: string;
  createdAt: string;
  lastUsedAt?: string;
  backedUp?: boolean;
  deviceType?: string;
}

export interface RevokeSessionResult {
  ok: boolean;
  current: boolean;
}

export interface PasskeyMutationResult {
  ok: boolean;
  passkeyID?: string;
}

export interface EmailVerificationRequestResult {
  ok: boolean;
  verified?: boolean;
  verificationToken?: string;
}

export interface AccountDataExport {
  exportedAt: string;
  account?: {
    id: string;
    email: string;
    emailVerifiedAt?: string;
    name: string;
    createdAt: string;
  };
  workspace?: {
    id: string;
    name: string;
    createdAt: string;
  };
  workspaces?: Array<{
    id: string;
    name: string;
    createdAt: string;
  }>;
  data: ReOSDatabase;
}

export interface LocalBackupImportResult {
  database: ReOSDatabase;
  imported: LocalBackupImportSummary;
  skipped: LocalBackupImportSkipped;
}

export interface BillingCheckoutInput {
  plan?: SubscriptionPlan;
  product?: "assistant_request_pack_250";
  successURL?: string;
  cancelURL?: string;
}

export interface AssistantUsageSummary {
  used: number;
  included: number;
  packRemaining: number;
  packPurchased: number;
  limit: number;
  remaining: number;
  resetsAt: string;
  breakdown: { conversations: number; automations: number; standard: number; advanced: number };
}

export interface BillingPortalInput {
  returnURL?: string;
}

export interface BillingSessionResult {
  id: string;
  url: string | null;
}

interface StoredLocalDemoDatabase {
  key: string;
  database: ReOSDatabase;
}

export function readStoredLocalDemoDatabase(storage: Pick<Storage, "getItem">): StoredLocalDemoDatabase | undefined {
  for (const key of [LOCAL_DEMO_STORAGE_KEY, ...LEGACY_LOCAL_DEMO_STORAGE_KEYS]) {
    const stored = storage.getItem(key);
    if (!stored) {
      continue;
    }
    return {
      key,
      database: normalizeDatabase(JSON.parse(stored) as Partial<ReOSDatabase>)
    };
  }
  return undefined;
}

export function saveStoredLocalDemoDatabase(storage: Pick<Storage, "setItem">, database: ReOSDatabase) {
  storage.setItem(LOCAL_DEMO_STORAGE_KEY, JSON.stringify(database));
}

export function clearStoredLocalDemoDatabase(storage: Pick<Storage, "removeItem">) {
  storage.removeItem(LOCAL_DEMO_STORAGE_KEY);
  for (const key of LEGACY_LOCAL_DEMO_STORAGE_KEYS) {
    storage.removeItem(key);
  }
}

export function accountDataExportForDatabase(database: ReOSDatabase, exportedAt = new Date().toISOString()): AccountDataExport {
  const user = database.users.find((candidate) => candidate.id === database.currentUserID);
  const workspace = database.workspaces.find((candidate) => candidate.id === database.activeWorkspaceID);
  return {
    exportedAt,
    account: user ? { id: user.id, email: user.email, emailVerifiedAt: user.emailVerifiedAt, name: user.name, createdAt: user.createdAt } : undefined,
    workspace: workspace ? { id: workspace.id, name: workspace.name, createdAt: workspace.createdAt } : undefined,
    workspaces: workspace ? [{ id: workspace.id, name: workspace.name, createdAt: workspace.createdAt }] : [],
    data: database
  };
}

export interface AccountWorkspaceSummary {
  id: string;
  name: string;
  defaultCurrency: string;
  role?: WorkspaceRole;
  createdAt: string;
  updatedAt: string;
}

interface AccountContext {
  account?: {
    id: string;
    email: string;
    emailVerifiedAt?: string;
    name: string;
    createdAt: string;
  };
  activeWorkspaceID?: string;
  workspaces?: AccountWorkspaceSummary[];
}

export interface DeleteAccountInput {
  confirmation: string;
}

export interface AccountDeletionResult {
  ok: boolean;
  deletedWorkspaceCount: number;
  deletedObjectCount: number;
}

export interface WorkspaceMemberSummary {
  id: string;
  userID: string;
  email: string;
  name: string;
  role: WorkspaceRole;
  current: boolean;
  joinedAt?: string;
  createdAt: string;
}

export interface WorkspaceMembersResult {
  members: WorkspaceMemberSummary[];
  limit: number;
}

export interface AddWorkspaceMemberInput {
  email: string;
  role: Exclude<WorkspaceRole, "owner">;
}

export interface WorkspaceMembersMutationResult extends WorkspaceMembersResult {
  database: ReOSDatabase;
}

export interface WorkspaceInvitationSummary {
  id: string;
  email: string;
  role: Exclude<WorkspaceRole, "owner">;
  invitedByName?: string;
  createdAt: string;
  expiresAt: string;
}

export interface WorkspaceInvitationsResult {
  invitations: WorkspaceInvitationSummary[];
  limit: number;
  invitationToken?: string;
}

export interface CreateWorkspaceInvitationInput {
  email: string;
  role: Exclude<WorkspaceRole, "owner">;
}

export interface WorkspaceInvitationMutationResult extends WorkspaceInvitationsResult {}

export interface AcceptWorkspaceInvitationResult {
  database: ReOSDatabase;
  members: WorkspaceMemberSummary[];
  invitations: WorkspaceInvitationSummary[];
  limit: number;
}

export function createReOSRepository(): ReOSRepository {
  return createReOSRepositoryForEnvironment(
    {
      apiBaseURL: import.meta.env.VITE_REOS_API_BASE_URL,
      allowLocalDemo: import.meta.env.VITE_REOS_ALLOW_LOCAL_DEMO,
      dev: import.meta.env.DEV
    },
    window.localStorage
  );
}

export function createReOSRepositoryForEnvironment(env: ReOSRepositoryEnvironment, storage: Storage): ReOSRepository {
  const configuredApiBaseURL = env.apiBaseURL?.trim();
  const apiBaseURL = normalizeApiBaseURL(configuredApiBaseURL);
  if (apiBaseURL) {
    return new HttpReOSRepository(apiBaseURL);
  }
  if (configuredApiBaseURL) {
    return new UnconfiguredRepository();
  }

  if (env.dev || env.allowLocalDemo?.trim().toLowerCase() === "true") {
    return new LocalDemoReOSRepository(storage);
  }

  return new UnconfiguredRepository();
}

function normalizeApiBaseURL(value: string | undefined) {
  if (!value) {
    return undefined;
  }
  try {
    const url = new URL(value);
    if (url.protocol !== "https:" && url.protocol !== "http:") {
      return undefined;
    }
    if (url.search || url.hash || isPlaceholderApiOrigin(value)) {
      return undefined;
    }
    const path = url.pathname.replace(/\/+$/, "");
    return `${url.origin}${path === "/" ? "" : path}`;
  } catch {
    return undefined;
  }
}

function isPlaceholderApiOrigin(value: string) {
  const normalized = value.toLowerCase();
  return normalized.includes("example.com") || normalized.includes("replace") || normalized.includes("<") || normalized.includes(">");
}

class LocalDemoReOSRepository implements ReOSRepository {
  mode: ReOSRepositoryMode = "local_demo";
  label = "Local demo storage";

  constructor(private readonly storage: Storage) {}

  async loadDatabase() {
    const stored = readStoredLocalDemoDatabase(this.storage);
    if (!stored) {
      return emptyDatabase();
    }
    if (stored.key !== LOCAL_DEMO_STORAGE_KEY) {
      saveStoredLocalDemoDatabase(this.storage, stored.database);
      this.storage.removeItem(stored.key);
    }
    return stored.database;
  }

  async loadWorkspace(workspaceID: string) {
    const database = await this.loadDatabase();
    const currentUserID = database.currentUserID;
    const canAccessWorkspace = database.workspaceMembers.some((member) => member.workspaceID === workspaceID && member.userID === currentUserID);
    if (!currentUserID || !canAccessWorkspace) {
      throw new Error("Workspace is not available for this account.");
    }
    const nextDatabase = {
      ...database,
      activeWorkspaceID: workspaceID
    };
    await this.saveDatabase(nextDatabase);
    return nextDatabase;
  }

  async listWorkspaceCollection(database: ReOSDatabase, resource: WorkspaceCollectionResource, query: WorkspaceCollectionQuery) {
    if (!database.activeWorkspaceID) throw new Error("Create or load a workspace first.");
    return paginateWorkspaceCollection(database, database.activeWorkspaceID, resource, query);
  }

  async listAccountWorkspaces(database?: ReOSDatabase) {
    const sourceDatabase = database ?? (await this.loadDatabase());
    return accountWorkspaceSummariesForDatabase(sourceDatabase);
  }

  async updateWorkspaceSettings(database: ReOSDatabase, update: WorkspaceSettingsUpdate) {
    const workspaceID = database.activeWorkspaceID;
    if (!workspaceID) {
      throw new Error("Create or load a workspace first.");
    }
    const nextDatabase = updateWorkspaceSettingsInDatabase(database, workspaceID, update);
    await this.saveDatabase(nextDatabase);
    return nextDatabase;
  }

  async createPasskeyRegistrationOptions(): Promise<PasskeyOptionsResult> {
    throw new Error("Passkeys are only available for cloud workspaces.");
  }

  async completePasskeyRegistration(): Promise<ReOSDatabase> {
    throw new Error("Passkeys are only available for cloud workspaces.");
  }

  async createPasskeyAuthenticationOptions(): Promise<PasskeyOptionsResult> {
    throw new Error("Passkeys are only available for cloud workspaces.");
  }

  async completePasskeyAuthentication(): Promise<ReOSDatabase> {
    throw new Error("Passkeys are only available for cloud workspaces.");
  }

  async requestEmailSignInCode(): Promise<EmailSignInCodeRequestResult> {
    throw new Error("Email sign-in codes are only available for cloud workspaces.");
  }

  async completeEmailSignInCode(): Promise<ReOSDatabase> {
    throw new Error("Email sign-in codes are only available for cloud workspaces.");
  }

  async signOut() {
    const database = await this.loadDatabase();
    await this.saveDatabase({ ...database, currentUserID: undefined, activeWorkspaceID: undefined });
  }

  async listSessions() {
    return [];
  }

  async revokeSession(): Promise<RevokeSessionResult> {
    return { ok: true, current: false };
  }

  async listPasskeys(): Promise<AccountPasskey[]> {
    return [];
  }

  async createAccountPasskeyRegistrationOptions(): Promise<PasskeyOptionsResult> {
    throw new Error("Passkey management is only available for cloud workspaces.");
  }

  async completeAccountPasskeyRegistration(): Promise<PasskeyMutationResult> {
    throw new Error("Passkey management is only available for cloud workspaces.");
  }

  async revokePasskey(): Promise<PasskeyMutationResult> {
    throw new Error("Passkey management is only available for cloud workspaces.");
  }

  async requestEmailVerification(): Promise<EmailVerificationRequestResult> {
    throw new Error("Email verification is only available for cloud workspaces.");
  }

  async completeEmailVerification(): Promise<void> {
    throw new Error("Email verification is only available for cloud workspaces.");
  }

  async exportAccountData(): Promise<AccountDataExport> {
    const database = await this.loadDatabase();
    return accountDataExportForDatabase(database);
  }

  accountArchiveDownloadURL(): string {
    throw new Error("Catalogue archive downloads are only available for cloud workspaces.");
  }

  async importLocalBackup(database: ReOSDatabase, backup: unknown): Promise<LocalBackupImportResult> {
    const currentUserID = database.currentUserID;
    const workspaceID = database.activeWorkspaceID;
    if (!currentUserID || !workspaceID) {
      throw new Error("Create or load a workspace first.");
    }
    const result = importLocalBackupIntoWorkspace({
      targetDatabase: database,
      currentUserID,
      workspaceID,
      backup
    });
    await this.saveDatabase(result.database);
    return result;
  }

  async deleteAccount(_input: DeleteAccountInput): Promise<AccountDeletionResult> {
    await this.clearDatabase();
    return { ok: true, deletedWorkspaceCount: 1, deletedObjectCount: 0 };
  }

  async createBillingCheckout(): Promise<BillingSessionResult> {
    throw new Error("Billing is only available for cloud workspaces.");
  }

  async getAssistantUsage(): Promise<AssistantUsageSummary> {
    return { used: 0, included: 0, packRemaining: 0, packPurchased: 0, limit: 0, remaining: 0, resetsAt: new Date().toISOString(), breakdown: { conversations: 0, automations: 0, standard: 0, advanced: 0 } };
  }

  async createBillingPortal(): Promise<BillingSessionResult> {
    throw new Error("Billing is only available for cloud workspaces.");
  }

  async getEbayIntegration(): Promise<EbayIntegrationStatus> {
    return disconnectedLocalEbayStatus();
  }

  async getEbayImportPreview(): Promise<EbayImportPreview> {
    return { page: 1, pageSize: 50, totalPages: 0, totalEntries: 0, items: [] };
  }

  async importEbayListings(): Promise<EbayImportResult> {
    throw new Error("eBay imports are only available for cloud workspaces.");
  }

  async getMarketplaceImportPreview(): Promise<MarketplaceImportPage> {
    throw new Error("Marketplace imports are only available for cloud workspaces.");
  }

  async getMarketplaceImportStatus(): Promise<MarketplaceImportStatus> {
    throw new Error("Marketplace imports are only available for cloud workspaces.");
  }

  async saveMarketplaceImportDecisions(): Promise<{ run: MarketplaceImportRun; records: MarketplaceImportRecord[] }> {
    throw new Error("Marketplace imports are only available for cloud workspaces.");
  }

  async applyMarketplaceImport(): Promise<MarketplaceImportApplyResult> {
    throw new Error("Marketplace imports are only available for cloud workspaces.");
  }

  async startEbayConnection(): Promise<string> {
    throw new Error("eBay connections are only available for cloud workspaces.");
  }

  async updateEbayIntegration(): Promise<EbayIntegrationStatus> {
    throw new Error("eBay connections are only available for cloud workspaces.");
  }

  async disconnectEbay(): Promise<EbayIntegrationStatus> {
    return disconnectedLocalEbayStatus();
  }

  async getEtsyIntegration(): Promise<EtsyIntegrationStatus> {
    return disconnectedLocalEtsyStatus();
  }

  async startEtsyConnection(): Promise<string> {
    throw new Error("Etsy connections are only available for cloud workspaces.");
  }

  async updateEtsyIntegration(): Promise<EtsyIntegrationStatus> {
    throw new Error("Etsy connections are only available for cloud workspaces.");
  }

  async disconnectEtsy(): Promise<EtsyIntegrationStatus> {
    return disconnectedLocalEtsyStatus();
  }

  async searchEtsyTaxonomy(): Promise<EtsyTaxonomyChoice[]> {
    throw new Error("Etsy taxonomy is only available for cloud workspaces.");
  }

  async listMarketplaceIntegrations(database: ReOSDatabase): Promise<MarketplaceAccount[]> {
    return database.marketplaceAccounts.filter((account) => account.workspaceID === database.activeWorkspaceID);
  }

  async runMarketplaceLifecycle(): Promise<MarketplaceLifecycleResult> {
    throw new Error("Marketplace account setup is only available for cloud workspaces.");
  }

  async reconcileMarketplaceLifecycle(): Promise<MarketplaceLifecycleResult> {
    throw new Error("Marketplace account reconciliation is only available for cloud workspaces.");
  }

  async listWorkspaceMembers(database: ReOSDatabase): Promise<WorkspaceMembersResult> {
    const workspaceID = database.activeWorkspaceID;
    const currentUserID = database.currentUserID;
    const members = database.workspaceMembers
      .filter((member) => member.workspaceID === workspaceID)
      .flatMap((member): WorkspaceMemberSummary[] => {
        const user = database.users.find((candidate) => candidate.id === member.userID);
        return user
          ? [
              {
                id: member.id,
                userID: user.id,
                email: user.email,
                name: user.name,
                role: member.role,
                current: user.id === currentUserID,
                joinedAt: member.joinedAt,
                createdAt: member.createdAt
              }
            ]
          : [];
      });
    return {
      members,
      limit: limitForEntitlement(database, workspaceID, "workspace_members")
    };
  }

  async addWorkspaceMember(): Promise<WorkspaceMembersMutationResult> {
    throw new Error("Team members are only available for cloud workspaces.");
  }

  async removeWorkspaceMember(): Promise<WorkspaceMembersMutationResult> {
    throw new Error("Team members are only available for cloud workspaces.");
  }

  async listWorkspaceInvitations(): Promise<WorkspaceInvitationsResult> {
    return {
      invitations: [],
      limit: Number.POSITIVE_INFINITY
    };
  }

  async createWorkspaceInvitation(): Promise<WorkspaceInvitationMutationResult> {
    throw new Error("Team invitations are only available for cloud workspaces.");
  }

  async revokeWorkspaceInvitation(): Promise<WorkspaceInvitationsResult> {
    throw new Error("Team invitations are only available for cloud workspaces.");
  }

  async acceptWorkspaceInvitation(): Promise<AcceptWorkspaceInvitationResult> {
    throw new Error("Team invitations are only available for cloud workspaces.");
  }

  async preparePhotoForSave(
    workspaceID: string,
    photo: PhotoInput,
    index: number,
    _context?: PhotoSaveContext
  ) {
    if (photo.assetID || photo.storedAsset) {
      return photo;
    }

    const now = new Date().toISOString();
    const assetID = makeID("asset");
    const fileName = (photo.fileName ?? `photo-${index + 1}.jpg`).replace(/[^a-zA-Z0-9._-]+/g, "-");
    const storedAsset: StoredAsset = {
      id: assetID,
      workspaceID,
      kind: storedAssetKindForPhoto(photo),
      provider: "local_demo",
      storageKey: `local/${workspaceID}/${assetID}/${fileName}`,
      publicURL: photo.url,
      mimeType: photo.mimeType ?? mimeTypeForPhotoURL(photo.url),
      byteSize: photo.byteSize ?? approximatePhotoByteSize(photo.url),
      width: photo.width,
      height: photo.height,
      createdAt: now,
      updatedAt: now
    };

    return {
      ...photo,
      assetID,
      storedAsset
    };
  }

  async cancelPreparedPhoto() {
    return true;
  }

  async createInventoryWorkflow(database: ReOSDatabase, input: InventoryItemInput, photos: PhotoInput[], selectedMarketplaces: Marketplace[]) {
    return createInventoryWorkflowInDatabase(database, input, photos, selectedMarketplaces);
  }

  async updateCatalogue(database: ReOSDatabase, input: CatalogueUpdateInput) { return applyCatalogueUpdate(database, input); }

  async updateInventoryItem(database: ReOSDatabase, itemID: string, update: InventoryItemUpdate) {
    return updateInventoryItemInDatabase(database, itemID, update);
  }

  async startItemResearch(): Promise<InventoryItemMutationResult> {
    throw new Error("AI research requires a connected Listwork workspace.");
  }

  async refreshItemResearch(): Promise<InventoryItemMutationResult> {
    throw new Error("AI research requires a connected Listwork workspace.");
  }

  async createListingDrafts(database: ReOSDatabase, itemID: string, selectedMarketplaces: Marketplace[]) {
    return createListingDraftsForItemInDatabase(database, itemID, selectedMarketplaces);
  }

  async addItemPhoto(database: ReOSDatabase, itemID: string, photo: PhotoInput) {
    return addItemPhotoToDatabase(database, itemID, photo);
  }

  async addItemPhotos(database: ReOSDatabase, itemID: string, photos: PhotoInput[]) {
    return addItemPhotosToDatabase(database, itemID, photos);
  }

  async updateItemPhoto(database: ReOSDatabase, photoID: string, photo: PhotoInput) {
    return updateItemPhotoInDatabase(database, photoID, photo);
  }

  async removeItemPhoto(database: ReOSDatabase, photoID: string) {
    return removeItemPhotoFromDatabase(database, photoID);
  }

  async updateListingDraft(database: ReOSDatabase, listingID: string, update: ListingDraftUpdate) {
    return updateListingDraftInDatabase(database, listingID, update);
  }

  async updateListingURL(database: ReOSDatabase, listingID: string, externalURL: string) {
    return {
      ...database,
      marketplaceListings: database.marketplaceListings.map((listing) =>
        listing.id === listingID ? { ...listing, externalURL: externalURL.trim() || undefined, updatedAt: new Date().toISOString() } : listing
      )
    };
  }

  async publishListing(): Promise<ListingPublishResult> {
    throw new Error("Direct marketplace listing requires a cloud workspace.");
  }

  async markListingLive(database: ReOSDatabase, listingID: string, externalURL?: string) {
    return markListingLiveInDatabase(database, listingID, externalURL);
  }

  async markItemsListedManually(database: ReOSDatabase, input: ManualListingInput) {
    return markItemsListedManuallyInDatabase(database, input);
  }

  async endListing(database: ReOSDatabase, listingID: string) {
    return {
      ...database,
      marketplaceListings: database.marketplaceListings.map((listing) =>
        listing.id === listingID ? { ...listing, status: "ended" as const, endedAt: new Date().toISOString(), updatedAt: new Date().toISOString() } : listing
      )
    };
  }

  async archiveInventoryItem(database: ReOSDatabase, itemID: string) {
    return archiveInventoryItemInDatabase(database, itemID);
  }

  async recordSale(database: ReOSDatabase, input: SaleInput) {
    return recordSaleInDatabase(database, input);
  }

  async updateSaleStatus(database: ReOSDatabase, saleID: string, status: SaleStatus) {
    return updateSaleStatusInDatabase(database, saleID, status);
  }

  async updateSaleFulfilment(database: ReOSDatabase, saleID: string, update: SaleFulfilmentUpdate) {
    return updateSaleStatusInDatabase(database, saleID, update);
  }

  async bulkPackSales(database: ReOSDatabase, saleIDs: string[]) {
    let nextDatabase = database;
    const updatedSaleIDs: string[] = [];
    const skipped: BulkSaleUpdateResult["skipped"] = [];
    for (const saleID of [...new Set(saleIDs)]) {
      try {
        nextDatabase = updateSaleStatusInDatabase(nextDatabase, saleID, { status: "packed" });
        updatedSaleIDs.push(saleID);
      } catch (error) {
        skipped.push({ saleID, reason: error instanceof Error ? error.message : "Order is not ready to pack." });
      }
    }
    await this.saveDatabase(nextDatabase);
    return { database: nextDatabase, updatedSaleIDs, skipped };
  }

  async markNotificationRead(database: ReOSDatabase, notificationID: string) {
    return {
      ...database,
      notifications: database.notifications.map((notification) =>
        notification.id === notificationID ? { ...notification, readAt: notification.readAt ?? new Date().toISOString() } : notification
      )
    };
  }

  async saveDatabase(database: ReOSDatabase) {
    saveStoredLocalDemoDatabase(this.storage, database);
  }

  async clearDatabase() {
    clearStoredLocalDemoDatabase(this.storage);
  }
}

class HttpReOSRepository implements ReOSRepository {
  mode: ReOSRepositoryMode = "api";
  label = "Workspace";
  private lastAccountContext?: AccountContext;
  private readonly replacementsRequiringSupersession = new Set<string>();

  constructor(private readonly apiBaseURL: string) {}

  async spreadsheetRequest<T>(database: ReOSDatabase, input: import("./spreadsheetMigration").MigrationRequest): Promise<T> {
    if (!database.activeWorkspaceID) throw new Error("Open a workspace first.");
    return this.postJson<T>(`/api/workspaces/${encodeURIComponent(database.activeWorkspaceID)}/spreadsheet-imports`, input);
  }

  async notificationSettingsRequest<T>(database: ReOSDatabase, method: "GET" | "POST" | "DELETE", action = "", body?: unknown): Promise<T> {
    const path = `/api/workspaces/${encodeURIComponent(database.activeWorkspaceID!)}/notification-settings${action ? `/${action}` : ""}`;
    const response = await this.request(path, {method, ...(body ? {headers:{"Content-Type":"application/json"},body:JSON.stringify(body)} : {})});
    return response.json() as Promise<T>;
  }

  async channelRequest<T>(database: ReOSDatabase, accountID: string, action: ChannelRequestAction, body?: unknown, query?: Record<string, string>): Promise<T> {
    if (!database.activeWorkspaceID) throw new Error("Open a workspace first.");
    const path = `/api/workspaces/${encodeURIComponent(database.activeWorkspaceID)}/channels/${encodeURIComponent(accountID)}/${action}`;
    return action === "activity" ? this.getJson<T>(`${path}?${new URLSearchParams(query)}`) : this.postJson<T>(path, body ?? {});
  }

  async loadDatabase() {
    const account = await this.readAccountContext();
    const workspaceID = account.activeWorkspaceID ?? account.workspaces?.[0]?.id;
    if (!workspaceID) {
      return normalizeDatabase({
        currentUserID: account.account?.id,
        users: account.account
          ? [
              {
                id: account.account.id,
                email: account.account.email,
                emailVerifiedAt: account.account.emailVerifiedAt,
                name: account.account.name,
                currency: "GBP",
                timezone: Intl.DateTimeFormat().resolvedOptions().timeZone,
                createdAt: account.account.createdAt,
                updatedAt: account.account.createdAt
              }
            ]
          : []
      });
    }
    return this.loadWorkspace(workspaceID);
  }

  async loadWorkspace(workspaceID: string, previous?: ReOSDatabase) {
    const workspace = normalizeDatabase(await this.getJson<ReOSDatabase>(`/api/workspaces/${encodeURIComponent(workspaceID)}`));
    return this.hydrateAssetReadURLs(reusePhotoReadURLs(workspace, previous));
  }

  async listWorkspaceCollection(database: ReOSDatabase, resource: WorkspaceCollectionResource, query: WorkspaceCollectionQuery) {
    const workspaceID = database.activeWorkspaceID;
    if (!workspaceID) throw new Error("Create or load a workspace first.");
    const params = new URLSearchParams();
    if (query.cursor) params.set("cursor", query.cursor);
    if (query.limit) params.set("limit", String(query.limit));
    for (const [key, value] of Object.entries(query.filters ?? {})) if (value) params.set(key, value);
    return this.getJson<WorkspaceCollectionPage>(`/api/workspaces/${encodeURIComponent(workspaceID)}/collections/${resource}?${params}`);
  }

  async listAccountWorkspaces() {
    const account = this.lastAccountContext ?? (await this.readAccountContext());
    return account.workspaces ?? [];
  }

  async updateWorkspaceSettings(database: ReOSDatabase, update: WorkspaceSettingsUpdate) {
    const workspaceID = database.activeWorkspaceID;
    if (!workspaceID) {
      throw new Error("Create or load a workspace first.");
    }
    const result = await this.patchJson<{ database: ReOSDatabase }>(`/api/workspaces/${encodeURIComponent(workspaceID)}`, update);
    this.lastAccountContext = undefined;
    return this.hydrateAssetReadURLs(normalizeDatabase(result.database));
  }

  async createPasskeyRegistrationOptions(input: PasskeyRegistrationOptionsInput) {
    return this.postJson<PasskeyOptionsResult>("/api/auth/passkeys/registration-options", input);
  }

  async completePasskeyRegistration(input: PasskeyCompletionInput) {
    this.lastAccountContext = undefined;
    const response = await this.request("/api/auth/passkeys/register", {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify(input)
    });
    return this.hydrateAssetReadURLs(normalizeDatabase((await response.json()) as Partial<ReOSDatabase>));
  }

  async createPasskeyAuthenticationOptions(input: PasskeyAuthenticationOptionsInput) {
    return this.postJson<PasskeyOptionsResult>("/api/auth/passkeys/authentication-options", input);
  }

  async completePasskeyAuthentication(input: PasskeyCompletionInput) {
    this.lastAccountContext = undefined;
    const response = await this.request("/api/auth/passkeys/authenticate", {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify(input)
    });
    return this.hydrateAssetReadURLs(normalizeDatabase((await response.json()) as Partial<ReOSDatabase>));
  }

  async requestEmailSignInCode(input: EmailSignInCodeRequestInput) {
    return this.postJson<EmailSignInCodeRequestResult>("/api/auth/email-code/request", input);
  }

  async completeEmailSignInCode(input: EmailSignInCodeCompleteInput) {
    this.lastAccountContext = undefined;
    const response = await this.request("/api/auth/email-code/complete", {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify(input)
    });
    return this.hydrateAssetReadURLs(normalizeDatabase((await response.json()) as Partial<ReOSDatabase>));
  }

  async signOut() {
    this.lastAccountContext = undefined;
    await this.request("/api/auth/logout", { method: "POST" });
  }

  async listSessions() {
    const response = await this.request("/api/auth/sessions", { method: "GET" });
    const body = (await response.json()) as { sessions?: AccountSession[] };
    return body.sessions ?? [];
  }

  async revokeSession(sessionID: string) {
    return this.deleteJson<RevokeSessionResult>(`/api/auth/sessions/${encodeURIComponent(sessionID)}`);
  }

  async listPasskeys() {
    const response = await this.request("/api/account/passkeys", { method: "GET" });
    const body = (await response.json()) as { passkeys?: AccountPasskey[] };
    return body.passkeys ?? [];
  }

  async createAccountPasskeyRegistrationOptions() {
    return this.postJson<PasskeyOptionsResult>("/api/account/passkeys/registration-options", {});
  }

  async completeAccountPasskeyRegistration(input: PasskeyCompletionInput) {
    return this.postJson<PasskeyMutationResult>("/api/account/passkeys/register", input);
  }

  async revokePasskey(passkeyID: string) {
    return this.deleteJson<PasskeyMutationResult>(`/api/account/passkeys/${encodeURIComponent(passkeyID)}`);
  }

  async requestEmailVerification() {
    return this.postJson<EmailVerificationRequestResult>("/api/auth/email-verification/request", {});
  }

  async completeEmailVerification(token: string) {
    await this.postJson<{ ok: boolean }>("/api/auth/email-verification/complete", { token });
  }

  async exportAccountData() {
    return this.getJson<AccountDataExport>("/api/account/export");
  }

  accountArchiveDownloadURL() {
    return `${this.apiBaseURL}/api/account/archive`;
  }

  async importLocalBackup(database: ReOSDatabase, backup: unknown) {
    const workspaceID = database.activeWorkspaceID;
    if (!workspaceID) {
      throw new Error("Create or load a workspace first.");
    }
    const result = await this.postJson<LocalBackupImportResult>(`/api/workspaces/${workspaceID}/imports/local-backup`, { backup });
    return {
      ...result,
      database: await this.hydrateAssetReadURLs(normalizeDatabase(result.database))
    };
  }

  async deleteAccount(input: DeleteAccountInput) {
    return this.deleteJson<AccountDeletionResult>("/api/account", input);
  }

  async createBillingCheckout(input: BillingCheckoutInput) {
    return this.postJson<BillingSessionResult>("/api/billing/checkout", input);
  }

  async getAssistantUsage(database: ReOSDatabase) {
    const workspaceID = database.activeWorkspaceID;
    if (!workspaceID) throw new Error("Select a workspace to inspect assistant usage.");
    return this.getJson<AssistantUsageSummary>(`/api/workspaces/${encodeURIComponent(workspaceID)}/assistant/usage`);
  }

  async createBillingPortal(input: BillingPortalInput = {}) {
    return this.postJson<BillingSessionResult>("/api/billing/customer-portal", input);
  }

  async getEbayIntegration(database: ReOSDatabase) {
    return this.getJson<EbayIntegrationStatus>(this.ebayIntegrationPath(database));
  }

  async getEbayImportPreview(database: ReOSDatabase, page = 1, pageSize = 50) {
    const path = `${this.ebayIntegrationPath(database)}/import-preview?page=${encodeURIComponent(page)}&pageSize=${encodeURIComponent(pageSize)}`;
    return this.getJson<EbayImportPreview>(path);
  }

  async importEbayListings(
    database: ReOSDatabase,
    input: { externalListingIDs: string[]; page: number; pageSize: number }
  ) {
    return this.postJson<EbayImportResult>(`${this.ebayIntegrationPath(database)}/import`, input);
  }

  async getMarketplaceImportPreview(
    database: ReOSDatabase,
    marketplace: Extract<Marketplace, "ebay" | "etsy">,
    page = 1,
    pageSize = 50,
    runID?: string
  ) {
    return this.postJson<MarketplaceImportPage>(`${this.marketplaceImportPath(database, marketplace)}/import-preview`, { page, pageSize, runID });
  }

  async getMarketplaceImportStatus(
    database: ReOSDatabase,
    marketplace: Extract<Marketplace, "ebay" | "etsy">,
    runID: string,
    recordIDs: string[]
  ) {
    const query = new URLSearchParams({ runID });
    recordIDs.forEach((recordID) => query.append("recordID", recordID));
    return this.getJson<MarketplaceImportStatus>(`${this.marketplaceImportPath(database, marketplace)}/import-status?${query}`);
  }

  async saveMarketplaceImportDecisions(
    database: ReOSDatabase,
    marketplace: Extract<Marketplace, "ebay" | "etsy">,
    runID: string,
    decisions: MarketplaceImportDecisionInput[]
  ) {
    return this.patchJson<{ run: MarketplaceImportRun; records: MarketplaceImportRecord[] }>(`${this.marketplaceImportPath(database, marketplace)}/import-decision`, { runID, decisions });
  }

  async applyMarketplaceImport(
    database: ReOSDatabase,
    marketplace: Extract<Marketplace, "ebay" | "etsy">,
    runID: string,
    recordIDs?: string[],
    expectedRevisions?: Record<string, number>,
    openingQuantities?: Record<string, number>
  ) {
    return this.postJson<MarketplaceImportApplyResult>(`${this.marketplaceImportPath(database, marketplace)}/import`, { runID, recordIDs, expectedRevisions, openingQuantities });
  }

  async startEbayConnection(database: ReOSDatabase, returnPath = "/channels", accessMode?: "read_only" | "read_write", financialAccess?: boolean) {
    const result = await this.postJson<{ authorizationURL: string }>(this.ebayIntegrationPath(database), { returnPath, accessMode, financialAccess });
    return result.authorizationURL;
  }

  async updateEbayIntegration(database: ReOSDatabase, input: EbayIntegrationConfiguration) {
    return this.patchJson<EbayIntegrationStatus>(this.ebayIntegrationPath(database), input);
  }

  async disconnectEbay(database: ReOSDatabase) {
    return this.deleteJson<EbayIntegrationStatus>(this.ebayIntegrationPath(database));
  }

  async getEtsyIntegration(database: ReOSDatabase) {
    return this.getJson<EtsyIntegrationStatus>(this.etsyIntegrationPath(database));
  }

  async startEtsyConnection(database: ReOSDatabase, returnPath = "/channels") {
    const result = await this.postJson<{ authorizationURL: string }>(this.etsyIntegrationPath(database), { returnPath });
    return result.authorizationURL;
  }

  async updateEtsyIntegration(database: ReOSDatabase, input: EtsyIntegrationConfiguration) {
    return this.patchJson<EtsyIntegrationStatus>(this.etsyIntegrationPath(database), input);
  }

  async disconnectEtsy(database: ReOSDatabase) {
    return this.deleteJson<EtsyIntegrationStatus>(this.etsyIntegrationPath(database));
  }

  async searchEtsyTaxonomy(database: ReOSDatabase, query: string) {
    const path = `${this.etsyIntegrationPath(database)}/taxonomy?query=${encodeURIComponent(query)}`;
    return (await this.getJson<{ choices: EtsyTaxonomyChoice[] }>(path)).choices;
  }

  async listMarketplaceIntegrations(database: ReOSDatabase) {
    const workspaceID = database.activeWorkspaceID;
    if (!workspaceID) {
      throw new Error("Create or load a workspace first.");
    }
    const result = await this.getJson<{ integrations: MarketplaceAccount[] }>(
      `/api/workspaces/${encodeURIComponent(workspaceID)}/marketplaces/integrations`
    );
    return result.integrations;
  }

  async runMarketplaceLifecycle(
    database: ReOSDatabase,
    marketplace: Marketplace,
    operation: MarketplaceLifecycleOperation,
    idempotencyKey?: string,
    payload?: Record<string, unknown>,
    resourceID?: string
  ) {
    const workspaceID = database.activeWorkspaceID;
    if (!workspaceID) {
      throw new Error("Create or load a workspace first.");
    }
    const account = database.marketplaceAccounts.find(
      (candidate) => candidate.workspaceID === workspaceID && candidate.marketplace === marketplace
    );
    const stableTransition = ["connect", "reconnect", "import_catalog", "pause", "disconnect"].includes(operation);
    const requestKey = idempotencyKey?.trim() || (
      stableTransition
        ? `${workspaceID}:${marketplace}:${operation}:g${account?.generation ?? 0}`
        : `${workspaceID}:${marketplace}:${operation}:${Date.now()}`
    );
    return this.postJson<MarketplaceLifecycleResult>(
      `/api/workspaces/${encodeURIComponent(workspaceID)}/marketplaces/${encodeURIComponent(marketplace)}/operations`,
      {
        operation,
        idempotencyKey: requestKey,
        resourceID,
        payload
      }
    );
  }

  async reconcileMarketplaceLifecycle(
    database: ReOSDatabase,
    marketplace: Marketplace,
    operation: MarketplaceLifecycleOperation,
    idempotencyKey: string
  ) {
    const workspaceID = database.activeWorkspaceID;
    if (!workspaceID) {
      throw new Error("Create or load a workspace first.");
    }
    return this.postJson<MarketplaceLifecycleResult>(
      `/api/workspaces/${encodeURIComponent(workspaceID)}/marketplaces/${encodeURIComponent(marketplace)}/operations`,
      {
        operation,
        idempotencyKey,
        reconcile: true
      }
    );
  }

  async listWorkspaceMembers(database: ReOSDatabase) {
    const workspaceID = database.activeWorkspaceID;
    if (!workspaceID) {
      throw new Error("Create or load a workspace first.");
    }
    return this.getJson<WorkspaceMembersResult>(`/api/workspaces/${workspaceID}/members`);
  }

  async addWorkspaceMember(database: ReOSDatabase, input: AddWorkspaceMemberInput) {
    const workspaceID = database.activeWorkspaceID;
    if (!workspaceID) {
      throw new Error("Create or load a workspace first.");
    }
    const result = await this.postJson<WorkspaceMembersMutationResult>(`/api/workspaces/${workspaceID}/members`, input);
    return {
      ...result,
      database: await this.hydrateAssetReadURLs(normalizeDatabase(result.database))
    };
  }

  async removeWorkspaceMember(database: ReOSDatabase, memberID: string) {
    const workspaceID = database.activeWorkspaceID;
    if (!workspaceID) {
      throw new Error("Create or load a workspace first.");
    }
    const result = await this.deleteJson<WorkspaceMembersMutationResult>(`/api/workspaces/${workspaceID}/members/${encodeURIComponent(memberID)}`);
    return {
      ...result,
      database: await this.hydrateAssetReadURLs(normalizeDatabase(result.database))
    };
  }

  async listWorkspaceInvitations(database: ReOSDatabase) {
    const workspaceID = database.activeWorkspaceID;
    if (!workspaceID) {
      throw new Error("Create or load a workspace first.");
    }
    return this.getJson<WorkspaceInvitationsResult>(`/api/workspaces/${workspaceID}/invitations`);
  }

  async createWorkspaceInvitation(database: ReOSDatabase, input: CreateWorkspaceInvitationInput) {
    const workspaceID = database.activeWorkspaceID;
    if (!workspaceID) {
      throw new Error("Create or load a workspace first.");
    }
    return this.postJson<WorkspaceInvitationMutationResult>(`/api/workspaces/${workspaceID}/invitations`, input);
  }

  async revokeWorkspaceInvitation(database: ReOSDatabase, invitationID: string) {
    const workspaceID = database.activeWorkspaceID;
    if (!workspaceID) {
      throw new Error("Create or load a workspace first.");
    }
    return this.deleteJson<WorkspaceInvitationsResult>(`/api/workspaces/${workspaceID}/invitations/${encodeURIComponent(invitationID)}`);
  }

  async acceptWorkspaceInvitation(token: string) {
    this.lastAccountContext = undefined;
    const result = await this.postJson<AcceptWorkspaceInvitationResult>("/api/workspace-invitations/accept", { token });
    return {
      ...result,
      database: await this.hydrateAssetReadURLs(normalizeDatabase(result.database))
    };
  }

  async preparePhotoForSave(
    workspaceID: string,
    photo: PhotoInput,
    index: number,
    context?: PhotoSaveContext
  ) {
    if (photo.storedAsset || photo.assetID) {
      context?.onProgress?.("ready");
      return photo;
    }

    const blob = await blobForPhoto(photo.url);
    const mimeType = photo.mimeType ?? blob.type ?? "image/jpeg";
    const byteSize = photo.byteSize ?? blob.size;
    const fileName = photo.fileName ?? defaultPhotoFileName(photo, index, mimeType);
    const replacementKey = context?.replacesPhotoID
      ? `${workspaceID}:${context.replacesPhotoID}`
      : undefined;
    const presign = await this.postJson<PresignedUpload>(`/api/workspaces/${workspaceID}/photos/presign`, {
      fileName,
      mimeType,
      byteSize,
      replacesPhotoID: context?.replacesPhotoID,
      supersedeExisting:
        replacementKey !== undefined &&
        this.replacementsRequiringSupersession.has(replacementKey)
    });

    try {
      context?.onProgress?.("uploading");
      const uploadResponse = await fetchPhotoUpload(
        presign.uploadURL,
        {
          method: presign.method,
          headers: presign.headers,
          body: blob
        },
        blob.size
      );
      if (!uploadResponse.ok) {
        throw new Error(`Photo upload failed with ${uploadResponse.status}.`);
      }

      context?.onProgress?.("checking");
      let storedAsset = await this.postJson<StoredAsset>(
        `/api/workspaces/${workspaceID}/photos/complete-upload`,
        {
          assetID: presign.assetID,
          storageKey: presign.storageKey,
          provider: presign.provider,
          kind: storedAssetKindForPhoto(photo),
          mimeType,
          byteSize,
          width: photo.width,
          height: photo.height
        },
        PHOTO_COMPLETION_REQUEST_TIMEOUT_MS
      );
      let displayURL = storedAsset.publicURL;
      if (!displayURL) {
        const readURL = await this.readURLForAsset(workspaceID, storedAsset.id);
        storedAsset = {
          ...storedAsset,
          readURL: readURL.url,
          signedURLExpiresAt: readURL.expiresAt
        };
        displayURL = readURL.url;
      }
      if (replacementKey) {
        this.replacementsRequiringSupersession.delete(replacementKey);
      }

      context?.onProgress?.("ready");
      return {
        ...photo,
        assetID: storedAsset.id,
        url: displayURL,
        thumbnailURL: displayURL,
        mimeType,
        byteSize,
        storedAsset
      };
    } catch (error) {
      let cancellationConfirmed = false;
      try {
        const cancellation = await this.deleteJson<{ cancelled: boolean }>(
          `/api/workspaces/${workspaceID}/photos/cancel`,
          { assetID: presign.assetID }
        );
        cancellationConfirmed = cancellation.cancelled;
      } catch {
        // Preserve the upload error; the next replacement attempt supersedes this reservation.
      }
      if (replacementKey) {
        if (cancellationConfirmed) {
          this.replacementsRequiringSupersession.delete(replacementKey);
        } else {
          this.replacementsRequiringSupersession.add(replacementKey);
        }
      }
      throw error;
    }
  }

  async cancelPreparedPhoto(workspaceID: string, photo: PhotoInput) {
    const assetID = photo.assetID ?? photo.storedAsset?.id;
    if (!assetID) {
      return true;
    }
    const result = await this.deleteJson<{ cancelled: boolean }>(
      `/api/workspaces/${workspaceID}/photos/cancel`,
      { assetID }
    );
    return result.cancelled;
  }

  async saveDatabase(database: ReOSDatabase) {
    void database;
    throw new Error("Production workspaces are saved through resource APIs, not whole-workspace snapshots.");
  }

  async createInventoryWorkflow(database: ReOSDatabase, input: InventoryItemInput, photos: PhotoInput[], selectedMarketplaces: Marketplace[]) {
    const workspaceID = database.activeWorkspaceID;
    if (!workspaceID) {
      throw new Error("Create or load a workspace first.");
    }
    const result = await this.postJson<InventoryWorkflowResult>(`/api/workspaces/${workspaceID}/inventory-workflows`, {
      input,
      photos,
      selectedMarketplaces
    });
    return {
      item: result.item,
      database: await this.hydrateAssetReadURLs(normalizeDatabase(result.database))
    };
  }

  async updateCatalogue(database: ReOSDatabase, input: CatalogueUpdateInput) {
    if (!database.activeWorkspaceID) throw new Error("Create or load a workspace first.");
    const result = await this.postJson<CatalogueUpdateResult>(`/api/workspaces/${database.activeWorkspaceID}/catalogue-updates`, input);
    return { ...result, database: await this.hydrateAssetReadURLs(normalizeDatabase(result.database)) };
  }

  async updateInventoryItem(database: ReOSDatabase, itemID: string, update: InventoryItemUpdate) {
    const workspaceID = database.activeWorkspaceID;
    if (!workspaceID) {
      throw new Error("Create or load a workspace first.");
    }
    const result = await this.patchJson<InventoryItemMutationResult>(
      `/api/workspaces/${workspaceID}/items/${encodeURIComponent(itemID)}`,
      update
    );
    return {
      item: result.item,
      database: await this.hydrateAssetReadURLs(normalizeDatabase(result.database))
    };
  }

  async startResearchBatch(database: ReOSDatabase, input: ResearchBatchInput): Promise<ResearchBatchResult> {
    const workspaceID = database.activeWorkspaceID;
    if (!workspaceID) throw new Error("Create or load a workspace first.");
    const result = await this.postJson<ResearchBatchResult>(`/api/workspaces/${workspaceID}/research-batches`, input);
    return { ...result, database: await this.hydrateAssetReadURLs(normalizeDatabase(result.database)) };
  }

  async startItemResearch(database: ReOSDatabase, itemID: string, input: ItemResearchRunRequest) {
    const workspaceID = database.activeWorkspaceID;
    if (!workspaceID) throw new Error("Create or load a workspace first.");
    const result = await this.postJson<InventoryItemMutationResult>(
      `/api/workspaces/${workspaceID}/items/${encodeURIComponent(itemID)}/research-runs`,
      input,
      ITEM_RESEARCH_REQUEST_TIMEOUT_MS
    );
    return {
      item: result.item,
      database: await this.hydrateAssetReadURLs(normalizeDatabase(result.database))
    };
  }

  async refreshItemResearch(database: ReOSDatabase, itemID: string) {
    const workspaceID = database.activeWorkspaceID;
    if (!workspaceID) throw new Error("Create or load a workspace first.");
    const result = await this.getJson<InventoryItemMutationResult>(
      `/api/workspaces/${workspaceID}/items/${encodeURIComponent(itemID)}/research-runs`
    );
    return {
      item: result.item,
      database: await this.hydrateAssetReadURLs(normalizeDatabase(result.database))
    };
  }

  async createListingDrafts(database: ReOSDatabase, itemID: string, selectedMarketplaces: Marketplace[]) {
    const workspaceID = database.activeWorkspaceID;
    if (!workspaceID) {
      throw new Error("Create or load a workspace first.");
    }
    const result = await this.postJson<ListingDraftsResult>(
      `/api/workspaces/${workspaceID}/items/${encodeURIComponent(itemID)}/listing-drafts`,
      { selectedMarketplaces }
    );
    return {
      listings: result.listings,
      database: await this.hydrateAssetReadURLs(normalizeDatabase(result.database))
    };
  }

  async addItemPhoto(database: ReOSDatabase, itemID: string, photo: PhotoInput) {
    const workspaceID = database.activeWorkspaceID;
    if (!workspaceID) {
      throw new Error("Create or load a workspace first.");
    }
    const result = await this.postJson<ItemPhotoResult>(`/api/workspaces/${workspaceID}/items/${encodeURIComponent(itemID)}/photos`, {
      photo
    });
    return {
      photo: result.photo,
      database: await this.hydrateAssetReadURLs(normalizeDatabase(result.database))
    };
  }

  async addItemPhotos(database: ReOSDatabase, itemID: string, photos: PhotoInput[]) {
    const workspaceID = database.activeWorkspaceID;
    if (!workspaceID) {
      throw new Error("Create or load a workspace first.");
    }
    const result = await this.postJson<ItemPhotosResult>(
      `/api/workspaces/${workspaceID}/items/${encodeURIComponent(itemID)}/photos`,
      { photos }
    );
    return {
      photos: result.photos,
      database: await this.hydrateAssetReadURLs(normalizeDatabase(result.database))
    };
  }

  async updateItemPhoto(database: ReOSDatabase, photoID: string, photo: PhotoInput) {
    const workspaceID = database.activeWorkspaceID;
    if (!workspaceID) {
      throw new Error("Create or load a workspace first.");
    }
    const result = await this.patchJson<ItemPhotoResult>(`/api/workspaces/${workspaceID}/photos/${encodeURIComponent(photoID)}`, {
      photo
    });
    return {
      photo: result.photo,
      database: await this.hydrateAssetReadURLs(normalizeDatabase(result.database))
    };
  }

  async removeItemPhoto(database: ReOSDatabase, photoID: string) {
    const workspaceID = database.activeWorkspaceID;
    if (!workspaceID) {
      throw new Error("Create or load a workspace first.");
    }
    const result = await this.deleteJson<{ database: ReOSDatabase }>(`/api/workspaces/${workspaceID}/photos/${encodeURIComponent(photoID)}`);
    return this.hydrateAssetReadURLs(normalizeDatabase(result.database));
  }

  async authorListing(database: ReOSDatabase, listingID: string, request: ListingAuthoringRequest): Promise<ListingAuthoringResult> {
    if (!database.activeWorkspaceID) throw new Error("Open a workspace first.");
    return this.postJson(`/api/workspaces/${database.activeWorkspaceID}/listings/${encodeURIComponent(listingID)}/authoring`, request, 35_000);
  }

  async updateListingDraft(database: ReOSDatabase, listingID: string, update: ListingDraftUpdate) {
    const workspaceID = database.activeWorkspaceID;
    if (!workspaceID) {
      throw new Error("Create or load a workspace first.");
    }
    const result = await this.patchJson<ListingDraftMutationResult>(
      `/api/workspaces/${workspaceID}/listings/${encodeURIComponent(listingID)}`,
      update
    );
    return {
      listing: result.listing,
      database: await this.hydrateAssetReadURLs(normalizeDatabase(result.database))
    };
  }

  async updateListingURL(database: ReOSDatabase, listingID: string, externalURL: string) {
    return this.updateListing(database, listingID, { externalURL });
  }

  async publishListing(database: ReOSDatabase, listingID: string): Promise<ListingPublishResult> {
    const workspaceID = database.activeWorkspaceID;
    if (!workspaceID) {
      throw new Error("Create or load a workspace first.");
    }
    const result = await this.postJson<ListingPublishResult>(`/api/workspaces/${workspaceID}/listings/${encodeURIComponent(listingID)}/publish`, {});
    return {
      publication: result.publication,
      database: await this.hydrateAssetReadURLs(normalizeDatabase(result.database))
    };
  }

  async markListingLive(database: ReOSDatabase, listingID: string, externalURL?: string) {
    return this.updateListing(database, listingID, { status: "live", externalURL });
  }

  async markItemsListedManually(database: ReOSDatabase, input: ManualListingInput) {
    const result = await this.postJson<ManualListingResult>(`/api/workspaces/${database.activeWorkspaceID}/listings/mark-manual`, input);
    return { ...result, database: await this.hydrateAssetReadURLs(normalizeDatabase(result.database)) };
  }

  async endListing(database: ReOSDatabase, listingID: string) {
    return this.updateListing(database, listingID, { status: "ended" });
  }

  async archiveInventoryItem(database: ReOSDatabase, itemID: string) {
    const workspaceID = database.activeWorkspaceID;
    if (!workspaceID) {
      throw new Error("Create or load a workspace first.");
    }
    const result = await this.patchJson<{ database: ReOSDatabase }>(`/api/workspaces/${workspaceID}/items/${encodeURIComponent(itemID)}`, { status: "archived" });
    return this.hydrateAssetReadURLs(normalizeDatabase(result.database));
  }

  async recordSale(database: ReOSDatabase, input: SaleInput) {
    const workspaceID = database.activeWorkspaceID;
    if (!workspaceID) {
      throw new Error("Create or load a workspace first.");
    }
    const result = await this.postJson<SaleWorkflowResult>(`/api/workspaces/${workspaceID}/sales`, input);
    return {
      sale: result.sale,
      database: await this.hydrateAssetReadURLs(normalizeDatabase(result.database))
    };
  }

  async updateSaleStatus(database: ReOSDatabase, saleID: string, status: SaleStatus) {
    const workspaceID = database.activeWorkspaceID;
    if (!workspaceID) {
      throw new Error("Create or load a workspace first.");
    }
    const result = await this.patchJson<{ database: ReOSDatabase }>(`/api/workspaces/${workspaceID}/sales/${saleID}`, { status });
    return this.hydrateAssetReadURLs(normalizeDatabase(result.database));
  }

  async updateSaleFulfilment(database: ReOSDatabase, saleID: string, update: SaleFulfilmentUpdate) {
    const workspaceID = database.activeWorkspaceID;
    if (!workspaceID) {
      throw new Error("Create or load a workspace first.");
    }
    const result = await this.patchJson<{ database: ReOSDatabase }>(`/api/workspaces/${workspaceID}/sales/${saleID}`, update);
    return this.hydrateAssetReadURLs(normalizeDatabase(result.database));
  }

  async bulkPackSales(database: ReOSDatabase, saleIDs: string[]) {
    const workspaceID = database.activeWorkspaceID;
    if (!workspaceID) throw new Error("Create or load a workspace first.");
    const result = await this.postJson<BulkSaleUpdateResult>(`/api/workspaces/${workspaceID}/sales/bulk`, { saleIDs, status: "packed" });
    return { ...result, database: await this.hydrateAssetReadURLs(normalizeDatabase(result.database)) };
  }

  async markNotificationRead(database: ReOSDatabase, notificationID: string) {
    const workspaceID = database.activeWorkspaceID;
    if (!workspaceID) {
      throw new Error("Create or load a workspace first.");
    }
    const result = await this.patchJson<{ database: ReOSDatabase }>(`/api/workspaces/${workspaceID}/notifications/${encodeURIComponent(notificationID)}/read`, {});
    return this.hydrateAssetReadURLs(normalizeDatabase(result.database));
  }

  async clearDatabase() {
    this.lastAccountContext = undefined;
    await this.request("/api/session", { method: "DELETE" });
  }

  private async readAccountContext() {
    const meResponse = await this.fetchApi("/api/me", { method: "GET" });
    if (meResponse.status === 401) {
      this.lastAccountContext = {};
      return this.lastAccountContext;
    }
    if (!meResponse.ok) {
      throw await apiError(meResponse);
    }
    this.lastAccountContext = (await meResponse.json()) as AccountContext;
    return this.lastAccountContext;
  }

  private async updateListing(database: ReOSDatabase, listingID: string, body: { status?: "live" | "ended"; externalURL?: string }) {
    const workspaceID = database.activeWorkspaceID;
    if (!workspaceID) {
      throw new Error("Create or load a workspace first.");
    }
    const result = await this.patchJson<{ database: ReOSDatabase }>(`/api/workspaces/${workspaceID}/listings/${listingID}`, body);
    return this.hydrateAssetReadURLs(normalizeDatabase(result.database));
  }

  private ebayIntegrationPath(database: ReOSDatabase) {
    const workspaceID = database.activeWorkspaceID;
    if (!workspaceID) {
      throw new Error("Create or load a workspace first.");
    }
    return `/api/workspaces/${encodeURIComponent(workspaceID)}/integrations/ebay`;
  }

  private etsyIntegrationPath(database: ReOSDatabase) {
    const workspaceID = database.activeWorkspaceID;
    if (!workspaceID) {
      throw new Error("Create or load a workspace first.");
    }
    return `/api/workspaces/${encodeURIComponent(workspaceID)}/integrations/etsy`;
  }

  private marketplaceImportPath(database: ReOSDatabase, marketplace: Extract<Marketplace, "ebay" | "etsy">) {
    return marketplace === "ebay" ? this.ebayIntegrationPath(database) : this.etsyIntegrationPath(database);
  }

  async refreshPhotoReadURLs(database: ReOSDatabase, forceAssetIDs: string[] = []) {
    const forced = new Set(forceAssetIDs);
    return this.hydrateAssetReadURLs({ ...database, storedAssets: database.storedAssets.map((asset) => forced.has(asset.id)
      ? { ...asset, readURL: undefined, signedURLExpiresAt: undefined } : asset) });
  }

  private async hydrateAssetReadURLs(database: ReOSDatabase): Promise<ReOSDatabase> {
    const workspaceID = database.activeWorkspaceID;
    if (!workspaceID) {
      return database;
    }
    const assetsNeedingReadURLs = database.storedAssets.filter(
      (asset) => asset.workspaceID === workspaceID && !asset.cleanupPendingAt && !asset.publicURL && needsFreshReadURL(asset)
    );

    const readURLs = new Map<string, AssetReadURL>();
    // Bound reads so a large gallery cannot overwhelm the API during startup.
    let nextAsset = 0;
    await Promise.all(Array.from({ length: Math.min(4, assetsNeedingReadURLs.length) }, async () => {
      while (nextAsset < assetsNeedingReadURLs.length) {
        const asset = assetsNeedingReadURLs[nextAsset++];
        try { readURLs.set(asset.id, await this.readURLForAsset(workspaceID, asset.id)); }
        catch { /* Keep saved photo metadata; the read-refresh hook retries later. */ }
      }
    }));
    const storedAssets = database.storedAssets.map((asset) => {
      const readURL = readURLs.get(asset.id);
      return readURL
        ? {
            ...asset,
            readURL: readURL.url,
            signedURLExpiresAt: readURL.expiresAt
          }
        : asset;
    });
    const assetURLs = new Map(
      storedAssets
        .filter((asset) => !asset.cleanupPendingAt)
        .map((asset) => [asset.id, asset.publicURL ?? asset.readURL])
        .filter((entry): entry is [string, string] => Boolean(entry[1]))
    );
    const cleanupPendingAssetIDs = new Set(
      storedAssets.filter((asset) => asset.cleanupPendingAt).map((asset) => asset.id)
    );
    return {
      ...database,
      storedAssets,
      itemPhotos: database.itemPhotos.map((photo) => {
        if (photo.assetID && cleanupPendingAssetIDs.has(photo.assetID)) {
          return {
            ...photo,
            url: UNAVAILABLE_PHOTO_URL,
            thumbnailURL: UNAVAILABLE_PHOTO_URL
          };
        }
        const displayURL = photo.assetID ? assetURLs.get(photo.assetID) : undefined;
        if (displayURL) {
          return {
            ...photo,
            url: displayURL,
            thumbnailURL: displayURL
          };
        }
        if (photo.assetID && (isAssetReferenceURL(photo.url) || isAssetReferenceURL(photo.thumbnailURL))) {
          return {
            ...photo,
            url: UNAVAILABLE_PHOTO_URL,
            thumbnailURL: UNAVAILABLE_PHOTO_URL
          };
        }
        return photo;
      })
    };
  }

  private async readURLForAsset(workspaceID: string, assetID: string) {
    return this.postJson<AssetReadURL>(`/api/workspaces/${workspaceID}/assets/${encodeURIComponent(assetID)}/read-url`, {});
  }

  private async request(path: string, init: RequestInit, timeoutMs = API_REQUEST_TIMEOUT_MS) {
    const response = await this.fetchApi(path, init, timeoutMs);
    if (!response.ok) {
      throw await apiError(response);
    }
    return response;
  }

  private async fetchApi(path: string, init: RequestInit, timeoutMs = API_REQUEST_TIMEOUT_MS) {
    const controller = new AbortController();
    const timeoutID = globalThis.setTimeout(() => controller.abort(), timeoutMs);
    try {
      return await fetch(`${this.apiBaseURL}${path}`, {
        credentials: "include",
        ...init,
        signal: controller.signal
      });
    } catch (error) {
      if (error instanceof Error && error.name === "AbortError") {
        throw new Error(WORKSPACE_UNREACHABLE_MESSAGE);
      }
      throw new Error(WORKSPACE_UNREACHABLE_MESSAGE);
    } finally {
      globalThis.clearTimeout(timeoutID);
    }
  }

  private async postJson<T>(path: string, body: unknown, timeoutMs = API_REQUEST_TIMEOUT_MS) {
    const response = await this.request(path, {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify(body)
    }, timeoutMs);
    return (await response.json()) as T;
  }

  private async patchJson<T>(path: string, body: unknown) {
    const response = await this.request(path, {
      method: "PATCH",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify(body)
    });
    return (await response.json()) as T;
  }

  private async getJson<T>(path: string) {
    const response = await this.request(path, { method: "GET" });
    return (await response.json()) as T;
  }

  private async deleteJson<T>(path: string, body?: unknown) {
    const response = await this.request(path, {
      method: "DELETE",
      headers: body === undefined ? undefined : { "Content-Type": "application/json" },
      body: body === undefined ? undefined : JSON.stringify(body)
    });
    return (await response.json()) as T;
  }
}

async function apiError(response: Response) {
  try {
    const body = (await response.json()) as { error?: string };
    return new ReOSApiError(body.error ?? serviceErrorForStatus(response.status), response.status);
  } catch {
    return new ReOSApiError(serviceErrorForStatus(response.status), response.status);
  }
}

function serviceErrorForStatus(status: number) {
  if (status === 401) {
    return "Sign in again to continue.";
  }
  if (status === 403) {
    return "You do not have access to that workspace.";
  }
  if (status === 404) {
    return "Listwork couldn't find that workspace record.";
  }
  if (status === 429) {
    return "Too many attempts. Wait a moment, then try again.";
  }
  if (status >= 500) {
    return "Listwork couldn't complete that action. Try again in a moment.";
  }
  return "Listwork couldn't complete that action. Check the details and try again.";
}

function workspaceServiceUnavailable(action: string): never {
  throw new Error(`This workspace is not connected, so Listwork cannot ${action}.`);
}

function disconnectedLocalEbayStatus(): EbayIntegrationStatus {
  return {
    configured: false,
    environment: "simulated",
    accessMode: "read_only",
    marketplaceID: "EBAY_GB",
    authorization: { authorized: false, grantedScopes: [], missingScopes: [] },
    notifications: { ready: false },
    choices: { paymentPolicies: [], fulfillmentPolicies: [], returnPolicies: [], locations: [] },
    readiness: { ready: false, missing: ["cloud workspace"] }
  };
}

function disconnectedLocalEtsyStatus(): EtsyIntegrationStatus {
  return {
    configured: false,
    environment: "disabled",
    authorization: { authorized: false, grantedScopes: [], missingScopes: [] },
    webhooks: { configured: false, endpoint: "" },
    choices: { shops: [], shippingProfiles: [], returnPolicies: [] },
    readiness: { ready: false, missing: ["cloud workspace"] }
  };
}

class UnconfiguredRepository implements ReOSRepository {
  mode: ReOSRepositoryMode = "unconfigured";
  label = "Workspace unavailable";

  async loadDatabase() {
    return emptyDatabase();
  }

  async loadWorkspace(): Promise<ReOSDatabase> {
    workspaceServiceUnavailable("load this workspace");
  }

  async listWorkspaceCollection(): Promise<WorkspaceCollectionPage> {
    workspaceServiceUnavailable("load workspace records");
  }

  async listAccountWorkspaces(): Promise<AccountWorkspaceSummary[]> {
    return [];
  }

  async updateWorkspaceSettings(): Promise<ReOSDatabase> {
    workspaceServiceUnavailable("update workspace settings");
  }

  async createPasskeyRegistrationOptions(): Promise<PasskeyOptionsResult> {
    workspaceServiceUnavailable("use passkeys");
  }

  async completePasskeyRegistration(): Promise<ReOSDatabase> {
    workspaceServiceUnavailable("use passkeys");
  }

  async createPasskeyAuthenticationOptions(): Promise<PasskeyOptionsResult> {
    workspaceServiceUnavailable("use passkeys");
  }

  async completePasskeyAuthentication(): Promise<ReOSDatabase> {
    workspaceServiceUnavailable("use passkeys");
  }

  async requestEmailSignInCode(): Promise<EmailSignInCodeRequestResult> {
    workspaceServiceUnavailable("send sign-in codes");
  }

  async completeEmailSignInCode(): Promise<ReOSDatabase> {
    workspaceServiceUnavailable("check sign-in codes");
  }

  async signOut() {
    return undefined;
  }

  async listSessions(): Promise<AccountSession[]> {
    return [];
  }

  async revokeSession(): Promise<RevokeSessionResult> {
    return { ok: true, current: false };
  }

  async listPasskeys(): Promise<AccountPasskey[]> {
    return [];
  }

  async createAccountPasskeyRegistrationOptions(): Promise<PasskeyOptionsResult> {
    workspaceServiceUnavailable("manage passkeys");
  }

  async completeAccountPasskeyRegistration(): Promise<PasskeyMutationResult> {
    workspaceServiceUnavailable("manage passkeys");
  }

  async revokePasskey(): Promise<PasskeyMutationResult> {
    workspaceServiceUnavailable("manage passkeys");
  }

  async requestEmailVerification(): Promise<EmailVerificationRequestResult> {
    workspaceServiceUnavailable("verify email addresses");
  }

  async completeEmailVerification(): Promise<void> {
    workspaceServiceUnavailable("verify email addresses");
  }

  async exportAccountData(): Promise<AccountDataExport> {
    workspaceServiceUnavailable("export account data");
  }

  accountArchiveDownloadURL(): string {
    return workspaceServiceUnavailable("download a catalogue archive");
  }

  async importLocalBackup(): Promise<LocalBackupImportResult> {
    workspaceServiceUnavailable("import account data");
  }

  async deleteAccount(): Promise<AccountDeletionResult> {
    workspaceServiceUnavailable("delete this account");
  }

  async createBillingCheckout(): Promise<BillingSessionResult> {
    workspaceServiceUnavailable("open plan review");
  }

  async getAssistantUsage(): Promise<AssistantUsageSummary> {
    workspaceServiceUnavailable("inspect assistant usage");
  }

  async createBillingPortal(): Promise<BillingSessionResult> {
    workspaceServiceUnavailable("open billing management");
  }

  async getEbayIntegration(): Promise<EbayIntegrationStatus> {
    workspaceServiceUnavailable("load the eBay connection");
  }

  async getEbayImportPreview(): Promise<EbayImportPreview> {
    workspaceServiceUnavailable("load eBay listings for import");
  }

  async importEbayListings(): Promise<EbayImportResult> {
    workspaceServiceUnavailable("import eBay listings");
  }

  async getMarketplaceImportPreview(): Promise<MarketplaceImportPage> {
    workspaceServiceUnavailable("review marketplace listings");
  }

  async getMarketplaceImportStatus(): Promise<MarketplaceImportStatus> {
    workspaceServiceUnavailable("check saved import results");
  }

  async saveMarketplaceImportDecisions(): Promise<{ run: MarketplaceImportRun; records: MarketplaceImportRecord[] }> {
    workspaceServiceUnavailable("save marketplace import choices");
  }

  async applyMarketplaceImport(): Promise<MarketplaceImportApplyResult> {
    workspaceServiceUnavailable("apply marketplace imports");
  }

  async startEbayConnection(): Promise<string> {
    workspaceServiceUnavailable("connect eBay");
  }

  async updateEbayIntegration(): Promise<EbayIntegrationStatus> {
    workspaceServiceUnavailable("configure eBay");
  }

  async disconnectEbay(): Promise<EbayIntegrationStatus> {
    workspaceServiceUnavailable("disconnect eBay");
  }

  async getEtsyIntegration(): Promise<EtsyIntegrationStatus> {
    workspaceServiceUnavailable("load the Etsy connection");
  }

  async startEtsyConnection(): Promise<string> {
    workspaceServiceUnavailable("connect Etsy");
  }

  async updateEtsyIntegration(): Promise<EtsyIntegrationStatus> {
    workspaceServiceUnavailable("configure Etsy");
  }

  async disconnectEtsy(): Promise<EtsyIntegrationStatus> {
    workspaceServiceUnavailable("disconnect Etsy");
  }

  async searchEtsyTaxonomy(): Promise<EtsyTaxonomyChoice[]> {
    workspaceServiceUnavailable("search Etsy taxonomy");
  }

  async listMarketplaceIntegrations(): Promise<MarketplaceAccount[]> {
    workspaceServiceUnavailable("load marketplace integrations");
  }

  async runMarketplaceLifecycle(): Promise<MarketplaceLifecycleResult> {
    workspaceServiceUnavailable("set up a marketplace account");
  }

  async reconcileMarketplaceLifecycle(): Promise<MarketplaceLifecycleResult> {
    workspaceServiceUnavailable("reconcile a marketplace operation");
  }

  async listWorkspaceMembers(): Promise<WorkspaceMembersResult> {
    workspaceServiceUnavailable("load team members");
  }

  async addWorkspaceMember(): Promise<WorkspaceMembersMutationResult> {
    workspaceServiceUnavailable("invite team members");
  }

  async removeWorkspaceMember(): Promise<WorkspaceMembersMutationResult> {
    workspaceServiceUnavailable("remove team members");
  }

  async listWorkspaceInvitations(): Promise<WorkspaceInvitationsResult> {
    workspaceServiceUnavailable("load team invitations");
  }

  async createWorkspaceInvitation(): Promise<WorkspaceInvitationMutationResult> {
    workspaceServiceUnavailable("invite team members");
  }

  async revokeWorkspaceInvitation(): Promise<WorkspaceInvitationsResult> {
    workspaceServiceUnavailable("cancel team invitations");
  }

  async acceptWorkspaceInvitation(): Promise<AcceptWorkspaceInvitationResult> {
    workspaceServiceUnavailable("accept team invitations");
  }

  async preparePhotoForSave(): Promise<PhotoInput> {
    workspaceServiceUnavailable("save photos");
  }

  async cancelPreparedPhoto(): Promise<boolean> {
    return true;
  }

  async createInventoryWorkflow(): Promise<InventoryWorkflowResult> {
    workspaceServiceUnavailable("create inventory");
  }

  async updateCatalogue(): Promise<CatalogueUpdateResult> { throw new Error("Open a workspace to complete inventory details."); }

  async updateInventoryItem(): Promise<InventoryItemMutationResult> {
    workspaceServiceUnavailable("update inventory");
  }

  async startItemResearch(): Promise<InventoryItemMutationResult> {
    workspaceServiceUnavailable("start item research");
  }

  async refreshItemResearch(): Promise<InventoryItemMutationResult> {
    workspaceServiceUnavailable("refresh item research");
  }

  async createListingDrafts(): Promise<ListingDraftsResult> {
    workspaceServiceUnavailable("create listing drafts");
  }

  async addItemPhoto(): Promise<ItemPhotoResult> {
    workspaceServiceUnavailable("add photos");
  }

  async addItemPhotos(): Promise<ItemPhotosResult> {
    workspaceServiceUnavailable("add photos");
  }

  async updateItemPhoto(): Promise<ItemPhotoResult> {
    workspaceServiceUnavailable("edit photos");
  }

  async removeItemPhoto(): Promise<ReOSDatabase> {
    workspaceServiceUnavailable("remove photos");
  }

  async updateListingDraft(): Promise<ListingDraftMutationResult> {
    workspaceServiceUnavailable("update listing drafts");
  }

  async updateListingURL(): Promise<ReOSDatabase> {
    workspaceServiceUnavailable("update listings");
  }

  async publishListing(): Promise<ListingPublishResult> {
    workspaceServiceUnavailable("list items on marketplaces");
  }

  async markListingLive(): Promise<ReOSDatabase> {
    workspaceServiceUnavailable("update listings");
  }

  async markItemsListedManually(): Promise<ManualListingResult> {
    workspaceServiceUnavailable("record manual listings");
  }

  async endListing(): Promise<ReOSDatabase> {
    workspaceServiceUnavailable("update listings");
  }

  async archiveInventoryItem(): Promise<ReOSDatabase> {
    workspaceServiceUnavailable("update inventory");
  }

  async recordSale(): Promise<SaleWorkflowResult> {
    workspaceServiceUnavailable("record sales");
  }

  async updateSaleStatus(): Promise<ReOSDatabase> {
    workspaceServiceUnavailable("update sales");
  }

  async updateSaleFulfilment(): Promise<ReOSDatabase> {
    workspaceServiceUnavailable("update orders");
  }

  async bulkPackSales(): Promise<BulkSaleUpdateResult> {
    workspaceServiceUnavailable("pack orders");
  }

  async markNotificationRead(): Promise<ReOSDatabase> {
    workspaceServiceUnavailable("update notifications");
  }

  async saveDatabase() {
    workspaceServiceUnavailable("save workspace data");
  }

  async clearDatabase() {
    return undefined;
  }
}

export function accountWorkspaceSummariesForDatabase(database: ReOSDatabase): AccountWorkspaceSummary[] {
  const currentUserID = database.currentUserID;
  return database.workspaces
    .flatMap((workspace): AccountWorkspaceSummary[] => {
      const membership = database.workspaceMembers.find((member) => member.workspaceID === workspace.id && member.userID === currentUserID);
      if (!membership) {
        return [];
      }
      return [
        {
          id: workspace.id,
          name: workspace.name,
          defaultCurrency: workspace.defaultCurrency,
          role: membership.role,
          createdAt: workspace.createdAt,
          updatedAt: workspace.updatedAt
        }
      ];
    })
    .sort((left, right) => {
      const updatedDelta = new Date(right.updatedAt).getTime() - new Date(left.updatedAt).getTime();
      return updatedDelta === 0 ? left.name.localeCompare(right.name) : updatedDelta;
    });
}

export function workspaceMemberSummariesForDatabase(database: ReOSDatabase): WorkspaceMemberSummary[] {
  const workspaceID = database.activeWorkspaceID;
  if (!workspaceID) {
    return [];
  }

  return database.workspaceMembers
    .filter((member) => member.workspaceID === workspaceID)
    .flatMap((member): WorkspaceMemberSummary[] => {
      const user = database.users.find((candidate) => candidate.id === member.userID);
      if (!user) {
        return [];
      }
      return [
        {
          id: member.id,
          userID: user.id,
          email: user.email,
          name: user.name,
          role: member.role,
          current: user.id === database.currentUserID,
          joinedAt: member.joinedAt,
          createdAt: member.createdAt
        }
      ];
    })
    .sort((left, right) => roleRank(left.role) - roleRank(right.role) || left.name.localeCompare(right.name));
}

function roleRank(role: WorkspaceRole) {
  return role === "owner" ? 0 : role === "admin" ? 1 : 2;
}

interface PresignedUpload {
  assetID: string;
  storageKey: string;
  method: "PUT";
  uploadURL: string;
  headers: Record<string, string>;
  provider: StoredAssetProvider;
}

interface AssetReadURL {
  assetID: string;
  url: string;
  expiresAt?: string;
  expiresInSeconds?: number;
  public?: boolean;
}

/** Reuse only unexpired URLs for unchanged assets in the same authenticated workspace. */
export function reusePhotoReadURLs(database: ReOSDatabase, previous?: ReOSDatabase): ReOSDatabase {
  if (!previous || database.activeWorkspaceID !== previous.activeWorkspaceID || database.currentUserID !== previous.currentUserID) return database;
  const cached = new Map(previous.storedAssets.map((asset) => [asset.id, asset]));
  return { ...database, storedAssets: database.storedAssets.map((asset) => {
    const old = cached.get(asset.id);
    if (!old?.readURL || !old.signedURLExpiresAt || Date.parse(old.signedURLExpiresAt) <= Date.now() + 60_000 || !Number.isFinite(Date.parse(old.signedURLExpiresAt))
      || asset.workspaceID !== database.activeWorkspaceID || old.workspaceID !== asset.workspaceID || asset.cleanupPendingAt
      || old.storageKey !== asset.storageKey || old.updatedAt !== asset.updatedAt || old.checksum !== asset.checksum) return asset;
    return { ...asset, readURL: old.readURL, signedURLExpiresAt: old.signedURLExpiresAt };
  }) };
}

export function needsFreshReadURL(asset: StoredAsset) {
  if (!asset.readURL) {
    return true;
  }
  if (!asset.signedURLExpiresAt) {
    return false;
  }
  const expiry = Date.parse(asset.signedURLExpiresAt);
  return !Number.isFinite(expiry) || expiry <= Date.now() + 60_000;
}

function isAssetReferenceURL(url: string | undefined) {
  return Boolean(url?.startsWith("reos-asset://"));
}

export function photoUploadTimeoutForBytes(byteSize: number) {
  const transferAllowance = Math.ceil(Math.max(0, byteSize) / PHOTO_UPLOAD_ASSUMED_BYTES_PER_SECOND * 1000);
  return Math.min(
    PHOTO_UPLOAD_MAX_TIMEOUT_MS,
    Math.max(PHOTO_UPLOAD_MIN_TIMEOUT_MS, PHOTO_UPLOAD_SETUP_ALLOWANCE_MS + transferAllowance)
  );
}

async function fetchPhotoUpload(url: string, init: RequestInit, byteSize: number) {
  const controller = new AbortController();
  const timeoutID = globalThis.setTimeout(() => controller.abort(), photoUploadTimeoutForBytes(byteSize));
  try {
    return await fetch(url, {
      ...init,
      signal: controller.signal
    });
  } catch (error) {
    if (abortError(error)) {
      throw new Error(PHOTO_UPLOAD_TIMEOUT_MESSAGE);
    }
    throw new Error("Photo upload couldn't reach storage. Check your connection and try again.");
  } finally {
    globalThis.clearTimeout(timeoutID);
  }
}

function abortError(error: unknown) {
  return Boolean(error && typeof error === "object" && "name" in error && error.name === "AbortError");
}

async function blobForPhoto(url: string) {
  if (/^data:/i.test(url)) {
    return blobFromDataURL(url);
  }

  let response: Response;
  try {
    response = await fetch(url);
  } catch {
    throw new Error("Unable to prepare this photo for upload.");
  }
  if (!response.ok) {
    throw new Error("Unable to prepare this photo for upload.");
  }
  return response.blob();
}

function blobFromDataURL(url: string) {
  const commaIndex = url.indexOf(",");
  if (commaIndex < 5) {
    throw new Error("Unable to prepare this photo for upload.");
  }

  const [mediaType = "", ...parameters] = url.slice(5, commaIndex).split(";");
  const mimeType = mediaType.trim() || "application/octet-stream";
  const encodedPayload = url.slice(commaIndex + 1);
  try {
    const payload = decodeURIComponent(encodedPayload);
    if (!parameters.some((parameter) => parameter.trim().toLowerCase() === "base64")) {
      return new Blob([payload], { type: mimeType });
    }

    const binary = globalThis.atob(payload);
    const bytes = new Uint8Array(binary.length);
    for (let index = 0; index < binary.length; index += 1) {
      bytes[index] = binary.charCodeAt(index);
    }
    return new Blob([bytes], { type: mimeType });
  } catch {
    throw new Error("Unable to prepare this photo for upload.");
  }
}

function mimeTypeForPhotoURL(url: string) {
  const match = /^data:([^;,]+)[;,]/i.exec(url);
  return match?.[1]?.trim() || "image/jpeg";
}

function approximatePhotoByteSize(url: string) {
  if (/^data:/i.test(url)) {
    try {
      return blobFromDataURL(url).size;
    } catch {
      return url.length;
    }
  }
  return new TextEncoder().encode(url).byteLength;
}

function defaultPhotoFileName(photo: PhotoInput, index: number, mimeType: string) {
  return `${photo.type || "photo"}-${index + 1}.${extensionForMimeType(mimeType)}`;
}

function extensionForMimeType(mimeType: string) {
  switch (mimeType) {
    case "image/png":
      return "png";
    case "image/webp":
      return "webp";
    case "image/heic":
      return "heic";
    default:
      return "jpg";
  }
}

function storedAssetKindForPhoto(photo: PhotoInput): StoredAssetKind {
  switch (photo.type) {
    case "background_removed":
      return "background_removed_photo";
    case "original":
      return "original_photo";
    case "cropped":
    case "edited":
      return "edited_photo";
  }
}
